import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import ReactFlow, {
  addEdge,
  Background,
  useEdgesState,
  useNodesState,
  useReactFlow,
  MarkerType,
  useViewport,
  useZoomPanHelper  
} from "react-flow-renderer";
import { useStore} from 'react-flow-renderer'
import { engineResummarize } from '../slm/engineBridge';
import { vocab } from '../slm/prompts';
import "./InfiniteCanvas.css";
import CustomCardNode from "./CustomCardNode";
import PostItNode from "./PostItNode";
import ResizableTextNode from "./ResizableTextNode";

import { useAuth } from "../contexts/AuthContext";
import { useT, useLang } from "../i18n/LanguageContext";
import theme from "../theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faRefresh,
  faNoteSticky,
  faFont,
  faImage,
  faLink,
  faFileArrowUp,faRightLeft, faObjectGroup,faShuffle,faMagicWandSparkles,
  faMagnifyingGlass, faFolderOpen, faFileExport, faEye, faEyeSlash, faClone,
  faArrowRotateLeft
} from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as SolidIcon } from "../assets/icons/line-solid.svg";
import { ReactComponent as DashedIcon } from "../assets/icons/line-dashed.svg";
import { ReactComponent as DottedIcon } from "../assets/icons/line-dotted.svg";

import html2canvas from 'html2canvas';
import { v4 as uuidv4 } from 'uuid';
import Chatbot from "../components/Chatbot"; // Import the Chatbot component
import BrainstormWidget from "../components/BrainstormPanel"; // Independent brainstorm panel
import MentorUpsell from "../components/MentorUpsell";
import TemplatePicker from "./TemplatePicker";
import ConceptBrief from "./ConceptBrief";
import CardDetailPopup from "./CardDetailPopup";
import CanvasCoachmarks from "./CanvasCoachmarks";
import EvidenceTray from "./EvidenceTray";
import { Snackbar, Alert, Button as MuiButton, Box, Popover, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Sparkle from "../components/Sparkle";
import ZoneLayer from "./ZoneLayer";
import MagicCursor, { CARD_W, CARD_H } from "./MagicCursor";
import {
  scenarioCardGridSlot, scenarioNoteGridSlot, scenarioBottomRowOriginY,
  isInScenarioCardGrid, isInScenarioNoteGrid, NOTE_W, NOTE_H,
  scenarioCardsGridHeight as computeScenarioCardsGridHeight,
  scenarioNotesGridHeight as computeScenarioNotesGridHeight,
  scenarioTreeCameraBounds,
} from "./scenarioCardGrid";
import AreaJumpButton from "./AreaJumpButton";
import DeckPileButton from "./DeckPileButton";
import Callout from "./Callout";
import ConfirmDialog from "./ConfirmDialog";
import { ZONE_BASE_POSITIONS, DT_ZONES, KANBAN_TOP, AREA_BOUNDS, getDashboardBounds } from "./dtZones";
import { STICKY_SHAPES } from "./stickyShapes";
import { NOTE_PALETTE, withTints, scenarioBranchColor } from "./colorPalette";
import { EMPTY_CANTIERE, EMPTY_FONDAMENTA } from "../slm/summarize";
import { pilePosition, snapToKanban, kanbanSlot, columnOf } from "./deckKanban";
import { draftKeyFor, isDraftExpired, draftHasMeaningfulContent } from "../lib/sessionDraft";
import { saveSessionToFile, openSessionFromFile } from "../lib/sessionFile";
import { registerDraftSaver, unregisterDraftSaver } from "../lib/draftRegistry";
import { fetchDeck } from "../data/deckApi";
import { relocalizeCardNodes, relocalizeCardList, moveCardToUsed } from "../lib/localizeCard";

const PHASE_COLOR = DT_ZONES.reduce((acc, z) => {
  acc[z.key] = z.color;
  return acc;
}, {});

// scenari_futuri card placement math lives in scenarioCardGrid.js, pulled
// out of this file so it's unit-testable (this file transitively imports
// firebase/auth, which breaks under jsdom).

// Marks the start of the scenario-branch block folded into the mentor
// brief's `direzione` field (scenari_futuri mode — see describeScenarioBranches
// and the onBrief handler below). Everything from this sentinel onward in the
// incoming direzione is stripped before a fresh fold is appended each turn,
// so the block is replaced rather than accumulating a duplicate copy every
// turn (the mentor's prompt asks it to update, not rewrite, the brief, so it
// likely echoes the prior fold text straight back).
const SCENARIO_BRANCHES_SENTINEL = "---scenario-branches---";



const nodeTypes = {
  customCardNode: CustomCardNode,
  postItNode: PostItNode,
  resizableText: ResizableTextNode,
};


// const initialNodes = generateInitialNodes();

// Already English in the Italian UI (technical edge-type/style words) — kept
// verbatim in it.json per the content rules, translated via t() anyway for
// consistency with the rest of this file's UI copy.
const getEdgeTypes = (t) => [
  { type: "default", label: t('canvas.edgeType.default') },
  { type: "straight", label: t('canvas.edgeType.straight') },
  { type: "step", label: t('canvas.edgeType.step') },
  { type: "smoothstep", label: t('canvas.edgeType.smoothstep') },
];

const getEdgeStyles = (t) => [
  { style: "solid", label: t('canvas.edgeStyle.solid') },
  { style: "dashed", label: t('canvas.edgeStyle.dashed') },
  { style: "dotted", label: t('canvas.edgeStyle.dotted') },
];

const styleIcons = {
  solid: <SolidIcon style={{ width: "10px", height: "10px" }} />,
  dashed: <DashedIcon style={{ width: "10px", height: "10px" }} />,
  dotted: <DottedIcon style={{ width: "10px", height: "10px" }} />,
};

// Visibility toggle chip: shows the group letter (or an icon for notes) and an
// eye / eye-slash to make the hide/show state obvious. `color` accents identity.
const ColorIcon = ({ color, label, isActive, onClick, icon, text, disabled }) => {
  const t = useT();
  const chipLabel = disabled
    ? t('canvas.groupChip.disabledHint')
    : (isActive ? t('canvas.groupChip.hide', { name: text || t('canvas.groupChip.groupFallback', { label }) })
                : t('canvas.groupChip.show', { name: text || t('canvas.groupChip.groupFallback', { label }) }));
  return (
  <button
    type="button"
    className={`group-chip${isActive ? " is-active" : ""}`}
    title={chipLabel}
    aria-label={chipLabel}
    onClick={() => onClick(label)}
    disabled={disabled}
    style={{ "--chip-accent": color }}
  >
    <span className="group-chip__id">
      {icon ? <FontAwesomeIcon icon={icon} /> : label}
    </span>
  </button>
  );
};


const getModeMeta = (t) => ({
  tradizionale: t('canvas.mode.tradizionale'),
  mappatura: t('canvas.mode.mappatura'),
  scenari_futuri: t('canvas.mode.scenariFuturi'),
});


const generateInitialNodes = (deckCards, role, isPro, onShowInfo, onCardNodeUpdate) => {
  const nodes = [];
  const cardWidth = 200;
  const cardHeight = 360;
  const startX = 50;
  const startY = 50;

  let currentRow = -1;
  let currentLetter = "";

  const availableCards = !isPro
    ? deckCards.filter(card => card.freePreview)
    : deckCards; // Pro and Admin users get all cards

  if (!isPro) {
    availableCards.forEach((card, index) => {
      nodes.push({
        id: card.id,
        type: "customCardNode",
        data: {
          label: card.label,
          image: card.image,
          backImage: card.back,
          group: card.groupId,
          descrizioneBreve: card.descrizione_breve,
          freePreview: card.freePreview,
          isFlipped: true,
          onShowInfo,
          onCardNodeUpdate,
        },
        position: pilePosition(index),
      });
    });
  } else {
    availableCards.forEach((card, index) => {
      const letter = card.groupId;
      if (letter !== currentLetter) {
        currentLetter = letter;
        currentRow++;
      }

      nodes.push({
        id: card.id,
        type: "customCardNode",
        data: {
          label: card.label,
          image: card.image,
          backImage: card.back,
          group: card.groupId,
          descrizioneBreve: card.descrizione_breve,
          freePreview: card.freePreview,
          isFlipped: true,
          onShowInfo,
          onCardNodeUpdate,
        },
        position: pilePosition(index),
      });
    });
  }

  return nodes;
};

// Shuffle function using Fisher-Yates algorithm
const shuffleArray = (array) => {
  let shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

// Strips the live update-callback functions before persisting a node (they
// can't survive JSON serialization and are re-attached on load). Shared by
// the Firestore manual save and the local draft snapshot.
function sanitizeNodesForStorage(nodes) {
  return nodes.map((node) => {
    if (node.type === 'customCardNode') {
      return {
        ...node,
        data: { ...node.data, onCardNodeUpdate: 'undefined', isFlipped: node.data.isFlipped },
      };
    }
    return { ...node, data: { ...node.data, onNodeUpdate: 'undefined', onNodeDelete: 'undefined' } };
  });
}

const InfiniteCanvas = () => {
  const t = useT();
  const { lang, dateLocale } = useLang();
  const [isChatbotVisible, setIsChatbotVisible] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [cardDetail, setCardDetail] = useState(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [stickyAnchor, setStickyAnchor] = useState(null);
  const [stickyShape, setStickyShape] = useState('rect');
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  // Cards search+filter, folded into the sidebar (was its own always-visible
  // top-center bar) — a popover instead of a permanent panel, same pattern
  // as fileMenuAnchor/stickyAnchor above.
  const [cardsPanelAnchor, setCardsPanelAnchor] = useState(null);
  const mentorBtnRef = useRef(null);
  const [mentorBtnRect, setMentorBtnRect] = useState(null);

  // The onboarding callout points at the Mentor toggle — measure its real
  // position instead of guessing a fixed offset, since the dock is
  // vertically centered (top:50%) and its height (and so the Mentor
  // button's exact top) shifts with viewport height and button count.
  useEffect(() => {
    const measure = () => {
      if (mentorBtnRef.current) {
        const r = mentorBtnRef.current.getBoundingClientRect();
        setMentorBtnRect({ top: r.top, left: r.right + 14 });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  // Session title editing. The title is the objective the mentor infers from
  // the chat (adopted once, then stable). titleEdited flips true when the user
  // renames it by hand or opens a file, after which the model never overwrites
  // it. editingTitle drives the inline pill input.
  const [titleEdited, setTitleEdited] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const titleInputRef = useRef(null);
  useEffect(() => { if (editingTitle && titleInputRef.current) titleInputRef.current.select(); }, [editingTitle]);
  const [brainstorm, setBrainstorm] = useState([]); // [{ id, card:{label,image,group}, html }]
  const [deckRevealed, setDeckRevealed] = useState(false);
  const [onboardDismissed, setOnboardDismissed] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const nudgedRef = useRef(false);
  const toggleChatbot = () => {
    setIsChatbotVisible((prev) => !prev);
  };

  // Queue of card moves the magic cursor is currently animating (flow coords).
  const [magicMoves, setMagicMoves] = useState(null);

  const [deck, setDeck] = useState(null); // null until fetchDeck() resolves
  // Refetched (already localized) whenever the UI language changes. A request
  // counter drops out-of-order responses: only the latest fetch may setDeck.
  const deckRequestRef = useRef(0);
  useEffect(() => {
    const reqId = ++deckRequestRef.current;
    fetchDeck(lang).then((d) => {
      if (reqId === deckRequestRef.current) setDeck(d);
    }).catch((e) => {
      console.error('fetchDeck failed:', e);
      // fail open to an empty deck rather than an infinite loader — but only
      // on the first load; a failed language refetch keeps the current deck.
      if (reqId === deckRequestRef.current) setDeck((prev) => prev || { groups: [], cards: [] });
    });
  }, [lang]);
  const deckReady = deck !== null;
  // Latest deck for callbacks that fire asynchronously (magic-cursor commit).
  const deckRef = useRef(deck);
  deckRef.current = deck;

  const [usedCards, setUsedCards] = useState([]);
  const [unusedCards, setUnusedCards] = useState([]);
  // Populate unusedCards once the deck has loaded (was previously a
  // synchronous initial-state derivation from the hardcoded cardNames array).
  // Later deck changes (language switch) only re-localize by card id: the
  // used/unused split and node positions are preserved. (setNodes is
  // declared below; it's only called when the effect runs, after render.)
  const deckLoadedRef = useRef(false);
  useEffect(() => {
    if (!deck) return;
    if (!deckLoadedRef.current) {
      deckLoadedRef.current = true;
      setUnusedCards(deck.cards.map(card => ({
        id: card.id,
        label: card.label,
        group: card.groupId,
        image: card.image,
        descrizioneBreve: card.descrizione_breve,
        freePreview: card.freePreview,
      })));
      return;
    }
    setUsedCards((l) => relocalizeCardList(l, deck.cards));
    setUnusedCards((l) => relocalizeCardList(l, deck.cards));
    setNodes((ns) => relocalizeCardNodes(ns, deck.cards));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck]);
  
  const { currentUser, role, isPro } = useAuth();
  const navigate = useNavigate();
  const userId = currentUser.uid;
  
  // Inside your component
  const [sessionId, setSessionId] = useState(uuidv4());
  const [sessionName, setSessionName] = useState("default_name");
  const [sessionBrief, setSessionBrief] = useState(null);
  const [sessionPhase, setSessionPhase] = useState("esplorazione_ideazione");
  const [sessionMode, setSessionMode] = useState(null);
  const [sessionModeAskAttempts, setSessionModeAskAttempts] = useState(0);
  const [pendingCheckpoint, setPendingCheckpoint] = useState(false);
  const [sessionTeacherStage, setSessionTeacherStage] = useState("osserva");
  const [sessionMessages, setSessionMessages] = useState([]);
  // Evidence keywords render as chips (Task 2, ZoneLayer.js), not canvas
  // nodes — evidenceChips is the source of truth, evidenceKeysRef (declared
  // below, near its dedup helpers) is a derived in-memory Set rebuilt from
  // it on every session load. Declared here (not near evidenceKeysRef)
  // because buildLocalSnapshot's dependency array references it and is
  // defined earlier in this component.
  const [evidenceChips, setEvidenceChips] = useState({});

 
  
  const [nodes, setNodes, rawOnNodesChange] = useNodesState([]);
  // Locked nodes (the mind-map Obiettivo post-it) can't be deleted via
  // keyboard/selection — filter out 'remove' changes targeting them before
  // they reach React Flow's internal reducer.
  const onNodesChange = useCallback((changes) => {
    const filtered = changes.filter((c) => {
      if (c.type !== 'remove') return true;
      const target = nodes.find((n) => n.id === c.id);
      return !(target && target.data && target.data.locked);
    });
    rawOnNodesChange(filtered);
  }, [nodes, rawOnNodesChange]);

  // Function to handle card node updates (flip, etc.) — declared here (not
  // further down with the other node-mutation callbacks) so the deck-load
  // effect below can reference it without a TDZ error, and so it's attached
  // directly at node-creation time instead of relying on a later effect that
  // never re-fires once the deck (and its cards) load asynchronously.
  const onCardNodeUpdate = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: newData };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Rebuild the starting deck when the effective tier flips (admin Free/Pro
  // preview). For normal users isPro/role are stable, so this only fires once.
  // Keyed on deckReady (not deck): a language refetch replaces `deck` and must
  // NOT reset the board — the deck effect above re-localizes in place instead.
  useEffect(() => {
    if (!deck) return;
    setNodes(generateInitialNodes(deck.cards, role, isPro, (d) => setCardDetail(d), onCardNodeUpdate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, role, deckReady]);

  // Declared here (not with the other useReactFlow() calls further down) so
  // handleCardAction below can reference it without a TDZ error.
  const { fitBounds: fitBoundsOnCardAction, getViewport: getViewportEarly, setViewport: setViewportEarly } = useReactFlow();
  // Same reason: mirrors ZoneLayer's own scenarioSize (the tree's live
  // measured size) up here so handleCardAction can place new cards/notes
  // below the tree's CURRENT height instead of a fixed offset that only
  // matched an empty tree — see scenarioCardGrid.js's scenarioBottomRowOriginY.
  const [scenarioTreeSize, setScenarioTreeSize] = useState({ width: 0, height: 0 });

  // scenari_futuri "cinematic beat": whenever the mentor proposes a fork or
  // drops cards/notes on the board, the camera zooms to it and a small
  // callout names what happened — one shared mechanism for both, since
  // they're the same shape (zoom in, notify, hold, zoom back out). `null`
  // when idle. While active: the callout shows `text`, a temporary "return"
  // icon appears (click = zoom back immediately), and a timer zooms back on
  // its own after SCENARIO_BEAT_HOLD_MS if the user doesn't click first.
  const [scenarioBeat, setScenarioBeat] = useState(null); // { text, prevViewport } | null
  const scenarioBeatTimerRef = useRef(null);
  const SCENARIO_BEAT_HOLD_MS = 3800;

  const endScenarioBeat = useCallback(() => {
    if (scenarioBeatTimerRef.current) {
      clearTimeout(scenarioBeatTimerRef.current);
      scenarioBeatTimerRef.current = null;
    }
    setScenarioBeat((prev) => {
      if (prev) setViewportEarly(prev.prevViewport, { duration: 900 });
      return null;
    });
  }, [setViewportEarly]);

  // Zooms to `bounds` and shows `text`, remembering the pre-zoom {x,y,zoom}
  // pose so endScenarioBeat can restore it exactly via setViewport. A
  // second beat firing while the first is still on-screen (e.g. a fork AND
  // cards in the same mentor turn) resets the hold timer and re-targets the
  // eventual return to whatever the pose was before the FIRST beat of the
  // turn, not the just-superseded intermediate one — otherwise a quick
  // back-to-back pair would "return" partway, to the first beat's frame,
  // instead of all the way out.
  const startScenarioBeat = useCallback((text, bounds) => {
    setScenarioBeat((prev) => ({ text, prevViewport: prev ? prev.prevViewport : getViewportEarly() }));
    fitBoundsOnCardAction(bounds, { padding: 0.3, duration: 900 });
    if (scenarioBeatTimerRef.current) clearTimeout(scenarioBeatTimerRef.current);
    scenarioBeatTimerRef.current = setTimeout(endScenarioBeat, SCENARIO_BEAT_HOLD_MS);
  }, [fitBoundsOnCardAction, getViewportEarly, endScenarioBeat]);

  // The mentor suggested cards: build an ordered flight plan (deck -> zone)
  // in canvas coordinates and hand it to the MagicCursor, which animates each
  // card and calls commitMagicMove() once it lands.
  const handleCardAction = useCallback((suggestedCards, suggestedNotes = []) => {
    const moves = [];

    // Seed each column's slot index from cards already sitting in it.
    const colCounts = {};
    nodes.forEach((n) => {
      if (n.type !== "customCardNode") return;
      const key = columnOf(n.position);
      if (key) colCounts[key] = (colCounts[key] || 0) + 1;
    });
    // scenari_futuri's own flowing-grid seeds (see scenarioCardGridSlot/
    // scenarioNoteGridSlot) — one running index each across the whole
    // cards/notes column, not per DT-zone, since this mode has no zone
    // columns to bucket into. Both columns' origin starts below the tree's
    // CURRENT measured height, with the guaranteed minimum gap.
    let scenarioCardGridIndex = nodes.filter(isInScenarioCardGrid).length;
    let scenarioNoteGridIndex = nodes.filter(isInScenarioNoteGrid).length;
    const scenarioBottomOriginY = scenarioBottomRowOriginY(scenarioTreeSize.height);

    suggestedCards.forEach((sc) => {
      const node = nodes.find(
        (n) => n.type === "customCardNode" && n.data.label === sc.label
      );
      if (!node) return;

      const zone = ZONE_BASE_POSITIONS[sc.zone] ? sc.zone : "integrazione_implementazione";
      const count = colCounts[zone] || 0;
      colCounts[zone] = count + 1;

      let toFlow, phaseColor;
      if (sessionMode === 'scenari_futuri') {
        toFlow = scenarioCardGridSlot(scenarioCardGridIndex++, scenarioBottomOriginY);
        phaseColor = PHASE_COLOR[zone];
      } else if (sessionMode === 'mappatura') {
        // No kanban column backgrounds in this mode either (see
        // ZoneLayer.js), so a kanbanSlot() position would float with
        // nothing to align to — scatter around the area center instead.
        const center = {
          x: AREA_BOUNDS.brainstorm.x + AREA_BOUNDS.brainstorm.w / 2,
          y: AREA_BOUNDS.brainstorm.y + AREA_BOUNDS.brainstorm.h / 2,
        };
        const angle = (colCounts[zone] * 47 + zone.length * 31) % 360; // deterministic scatter, no RNG needed
        const radius = 260 + (colCounts[zone] % 4) * 90;
        toFlow = {
          x: center.x + Math.cos(angle * Math.PI / 180) * radius,
          y: center.y + Math.sin(angle * Math.PI / 180) * radius,
        };
        phaseColor = PHASE_COLOR[zone];
      } else {
        toFlow = kanbanSlot(zone, count);
        phaseColor = undefined;
      }

      moves.push({
        kind: "card",
        nodeId: node.id,
        label: node.data.label,
        group: node.data.group,
        image: node.data.image,
        descrizioneBreve: node.data.descrizioneBreve,
        fromFlow: { ...node.position },
        toFlow,
        phaseColor,
      });
    });

    // A mentor-authored note has no existing card to place — stack it in a
    // lane to the right of its phase's card column (clear of both the card
    // stack and the evidence keyword-chip grid above it), a separate
    // counter so repeated turns don't overlap earlier notes.
    suggestedNotes.forEach((sn) => {
      const text = (sn.text || "").trim();
      if (!text) return;
      const phase = ZONE_BASE_POSITIONS[sn.phase] ? sn.phase : "integrazione_implementazione";
      let toFlow;
      if (sessionMode === 'scenari_futuri') {
        // Own column (bottom-right of the T), not a kanban-zone lane — this
        // mode renders no kanban columns for base.x to mean anything.
        toFlow = scenarioNoteGridSlot(scenarioNoteGridIndex++, scenarioBottomOriginY);
      } else {
        const idx = mentorNoteCountRef.current[phase] || 0;
        mentorNoteCountRef.current[phase] = idx + 1;
        const base = ZONE_BASE_POSITIONS[phase];
        toFlow = { x: base.x + CARD_W + 20, y: KANBAN_TOP + idx * 160 };
      }
      moves.push({
        kind: "note",
        phase,
        text,
        toFlow,
      });
    });

    if (moves.length === 0) return;
    setMagicMoves(moves);
    // Frame both the deck (card origin) and every landing zone in one shot
    // instead of hard-cutting to a fixed Brainstorming-only view — only
    // when this batch actually includes a card. A note's drop-in is
    // entirely local to its column; nothing off-screen needs revealing.
    const cardMoves = moves.filter((m) => m.kind === "card");
    const noteMoves = moves.filter((m) => m.kind === "note");
    if (sessionMode === 'scenari_futuri') {
      // Notes DO need framing here too, unlike the generic case above —
      // this mode's notes column can be far from wherever the camera
      // happens to be, not "nothing off-screen" the way a tradizionale
      // kanban-lane note drop is.
      if (cardMoves.length === 0 && noteMoves.length === 0) return;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      cardMoves.forEach((m) => {
        [m.fromFlow, m.toFlow].forEach((p) => {
          minX = Math.min(minX, p.x);
          minY = Math.min(minY, p.y);
          maxX = Math.max(maxX, p.x + CARD_W);
          maxY = Math.max(maxY, p.y + CARD_H);
        });
      });
      noteMoves.forEach((m) => {
        minX = Math.min(minX, m.toFlow.x);
        minY = Math.min(minY, m.toFlow.y);
        maxX = Math.max(maxX, m.toFlow.x + NOTE_W);
        maxY = Math.max(maxY, m.toFlow.y + NOTE_H);
      });
      const parts = [];
      if (cardMoves.length) parts.push(t('canvas.scenarioBeat.cards', { count: cardMoves.length }));
      if (noteMoves.length) parts.push(t('canvas.scenarioBeat.notes', { count: noteMoves.length }));
      startScenarioBeat(
        t('canvas.scenarioBeat.mentorAdded', { items: parts.join(t('canvas.scenarioBeat.joiner')) }),
        { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
      );
    } else if (cardMoves.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      cardMoves.forEach((m) => {
        [m.fromFlow, m.toFlow].forEach((p) => {
          minX = Math.min(minX, p.x);
          minY = Math.min(minY, p.y);
          maxX = Math.max(maxX, p.x + CARD_W);
          maxY = Math.max(maxY, p.y + CARD_H);
        });
      });
      fitBoundsOnCardAction(
        { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
        { padding: 0.25, duration: 900 }
      );
    }
  }, [nodes, fitBoundsOnCardAction, sessionMode, scenarioTreeSize, startScenarioBeat, t]);

  // Called by MagicCursor when a single card finishes its flight. Landing on
  // the board is the same "placed" moment as a manual drag: front-face, and
  // excluded from "gira tutte" (which only touches the still-in-pile deck).
  const commitMagicMove = useCallback((move) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === move.nodeId
          ? { ...node, position: move.toFlow, data: { ...node.data, isFlipped: false, isUsed: true, phaseColor: move.phaseColor } }
          : node
      )
    );
    // By card id, with label/image from the deck current at commit time
    // (deckRef): a language switch mid-flight must not leave the card in
    // both lists or carrying a stale-language label.
    const deckCards = deckRef.current ? deckRef.current.cards : null;
    setUsedCards((prev) => moveCardToUsed(prev, [], move, deckCards).used);
    setUnusedCards((prev) => moveCardToUsed([], prev, move, deckCards).unused);
  }, [setNodes]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // const [newNotePosition, setNewNotePosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeGroups, setActiveGroups] = useState(["note"]);
  // Once the deck loads, default every fetched group to visible (matches the
  // old hardcoded A-E-visible-by-default behavior).
  useEffect(() => {
    if (!deck) return;
    setActiveGroups((prev) => Array.from(new Set([...deck.groups.map((g) => g.id), ...prev])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck]);
  const edgeTypes = useMemo(() => getEdgeTypes(t), [t]);
  const edgeStyles = useMemo(() => getEdgeStyles(t), [t]);
  const [currentEdgeType, setCurrentEdgeType] = useState(() => getEdgeTypes(t)[0]);
  const [currentEdgeStyle, setCurrentEdgeStyle] = useState(() => getEdgeStyles(t)[0]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance_, setReactFlowInstance] = useState(null);
  const { fitView, project } = useReactFlow(); /*sasa*/
  const reactFlowInstance = useReactFlow();
  const { x, y, zoom } = useViewport();
  
  // const { currentUser } = useAuth();
  
  
  // const nodes = useNodes();
  const { setViewport } = useReactFlow(); /*sasa*/
  // console.log('-->user id ' + userId)

  // funzioni per salvataggio e recupero sessione

  const [saveFeedback, setSaveFeedback] = useState("");
  // The save-status toast (success or error) was never dismissed — auto-clear it.
  useEffect(() => {
    if (!saveFeedback) return;
    const t = setTimeout(() => setSaveFeedback(""), 4000);
    return () => clearTimeout(t);
  }, [saveFeedback]);
  // Live handle to the on-disk session file (File System Access API), so
  // "Salva" re-writes the same file the user opened/saved to. null until the
  // first save/open, or in download/upload fallback mode.
  const sessionFileHandleRef = useRef(null);

  const resetNodePositions = () => {
    if (!deck) return;
    const initialPositions = generateInitialPositions(deck.cards);
    setNodes((nds) =>
      nds.map((node, index) => {
        if (node.type === "customCardNode") {
          return {
            ...node,
            position: initialPositions[index],
          };
        }
        return node;
      })
    );
    // Cards return to the deck → nothing is "in play", so reset the used/unused
    // split (keeps the brainstorm card picker in sync, like createDeck does).
    const allCards = nodes
      .filter((node) => node.type === "customCardNode")
      .map((node) => ({
        id: node.id,
        label: node.data.label,
        group: node.data.group,
        image: node.data.image,
        descrizioneBreve: node.data.descrizioneBreve,
        freePreview: node.data.freePreview,
      }));
    setUsedCards([]);
    setUnusedCards(allCards);
  };

  // Flip the deck face-up and fan it into the grouped A–E grid, one card
  // after another (casino-dealer style) instead of a single instant jump —
  // each card gets its own short delay, a transient CSS transition (cleared
  // once it lands so a later manual drag isn't laggy), and reuses
  // CustomCardNode's existing flip transition (transform 0.6s on isFlipped).
  const DEAL_STEP_MS = 40;
  const DEAL_TRANSITION_MS = 350;
  const revealDeck = useCallback(() => {
    if (!deck) return;
    const grid = generateInitialPositions(deck.cards);
    setNodes((nds) => {
      const cardIds = nds.filter((n) => n.type === "customCardNode").map((n) => n.id);
      cardIds.forEach((id, i) => {
        setTimeout(() => {
          setNodes((cur) => cur.map((n) => (
            n.id === id
              ? { ...n, position: grid[i], style: { transition: `transform ${DEAL_TRANSITION_MS}ms ease` }, data: { ...n.data, isFlipped: false } }
              : n
          )));
        }, i * DEAL_STEP_MS);
        setTimeout(() => {
          setNodes((cur) => cur.map((n) => (
            n.id === id && n.style ? { ...n, style: undefined } : n
          )));
        }, i * DEAL_STEP_MS + DEAL_TRANSITION_MS);
      });
      return nds;
    });
    setDeckRevealed(true);
  }, [setNodes, deck]);

  // Initialize nodes only on the first render
  useEffect(() => {
    setNodes((nds) =>
        nds.map((node) => {
            if (node.type === "customCardNode" && !node.data.onCardNodeUpdate) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        onCardNodeUpdate,  // Attach the onCardNodeUpdate function
                        onShowInfo: (d) => setCardDetail(d),
                    },
                };
            }
            return node;
        })
    );
}, [onCardNodeUpdate, setNodes]);

  //customcardnodestyle
  // const customNodeStyles = (isUsed) => ({
  //   border: isUsed ? 'none' : '2px solid #0056b3',
  //   transition: 'border 0.3s ease'
  // });

// on node drag
// Add this to track card movements
const [updateCount, setUpdateCount] = useState(0); // State to force re-render

const onNodeDragStop = useCallback((event, node) => {
  if (node.type === 'customCardNode') {
    const nodeData = nodes.find(n => n.id === node.id);
    if (!nodeData) return;

    // Update usedCards and unusedCards immediately
    setUsedCards(prevUsedCards => {
      // Check if the card is already in usedCards
      if (!prevUsedCards.some(card => card.id === nodeData.id)) {
        // Add the entire card object
        return [...prevUsedCards, {
          id: nodeData.id,
          label: nodeData.data.label,
          group: nodeData.data.group,
          image: nodeData.data.image,
          backImage: nodeData.data.backImage, // Include back image if needed
          descrizioneBreve: nodeData.data.descrizioneBreve,
        }];
      }
      return prevUsedCards; // Return the previous state if already used
    });

    setUnusedCards(prevUnusedCards => {
      // Remove the card from unused cards
      return prevUnusedCards.filter(c => c.id !== nodeData.id);
    });

    // Kanban: if dropped inside a brainstorming column, snap to the bottom of its list.
    // A card placed on the board is always front-face and can no longer be flipped
    // (isUsed disables the double-click flip in CustomCardNode).
    setNodes((nds) => {
      // Kanban columns are a tradizionale-only concept — mappatura already
      // skips this, and scenari_futuri must too: it renders no DT_ZONES
      // (see ZoneLayer.js), but the phantom column hit-regions still cover
      // the same brainstorm area, so a card dropped there was silently
      // snapping into a kanban slot that then sat physically underneath the
      // scenario timeline/matrix's own opaque UI — which escapes react-flow's
      // stacking context (z-index 6, see ZoneLayer.js's isScenarioMode
      // comment) specifically to render above card nodes (z-index 4),
      // visually hiding the card behind it.
      const snapped = (sessionMode === 'mappatura' || sessionMode === 'scenari_futuri')
        ? null
        : snapToKanban(node.position, nds, node.id);
      return nds.map((n) =>
        n.id === node.id
          ? { ...n, position: snapped || n.position, data: { ...n.data, isFlipped: false, isUsed: true } }
          : n
      );
    });

    // Log the updated state for debugging
    console.log('Updated usedCards:', [...usedCards, nodeData]);
    console.log('Updated unusedCards:', unusedCards);
  }
}, [nodes, sessionMode]);

  //Initial Positions
  const generateInitialPositions = (deckCards) => {
    const positions = [];
    const cardWidth = 200;
    const cardHeight = 360;
    const startX = 50;
    const startY = 50;

    let currentRow = -1;
    let currentLetter = "";

    deckCards.forEach((card, index) => {
      const letter = card.groupId;
      if (letter !== currentLetter) {
        currentLetter = letter;
        currentRow++;
      }
  
      const column = index % 8;
      const x = startX + column * (cardWidth + 10);
      const y = startY + currentRow * (cardHeight + 10);
  
      positions.push({ x, y });
    });
  
    return positions;
  };
  

  //Initial Positions


  // Save/open the session as a local file the user owns — no server copy.
  // "Salva" re-writes the open file (or prompts the first time); "Salva
  // come…" always prompts for a new file. See src/lib/sessionFile.js.
  const stripSessionExt = (fileName) =>
    (fileName || "").replace(/\.sinergie\.json$/i, "").replace(/\.json$/i, "");

  const saveSessionFile = async (promptNew = false) => {
    try {
      const snapshot = { ...buildLocalSnapshot(), name: sessionName };
      const res = await saveSessionToFile(snapshot, promptNew ? null : sessionFileHandleRef.current, t);
      if (!res) return; // user cancelled the picker
      if (res.handle) sessionFileHandleRef.current = res.handle;
      const base = stripSessionExt(res.name);
      if (base) setSessionName(base);
      setSaveFeedback(res.downloaded ? t('canvas.saveFeedback.downloaded') : t('canvas.saveFeedback.saved'));
    } catch (e) {
      console.error("save session file failed:", e);
      setSaveFeedback(t('canvas.saveFeedback.saveError'));
    }
  };

  const openSessionFile = async () => {
    try {
      const res = await openSessionFromFile(t);
      if (!res) return; // user cancelled
      const { data, handle, name } = res;
      const rehydratedNodes = (Array.isArray(data.nodes) ? data.nodes : []).map((node) =>
        node.type === "customCardNode"
          ? { ...node, data: { ...node.data, onCardNodeUpdate, onShowInfo: (d) => setCardDetail(d) } }
          : { ...node, data: { ...node.data, onNodeUpdate, onNodeDelete } }
      );
      setSessionId(data.sessionId || uuidv4());
      // Saved sessions carry the labels of the language they were saved in —
      // re-localize card nodes by id to the active deck language.
      setNodes(deck ? relocalizeCardNodes(rehydratedNodes, deck.cards) : rehydratedNodes);
      setEdges(Array.isArray(data.edges) ? data.edges : []);
      setBrainstorm(Array.isArray(data.brainstorm) ? data.brainstorm : []);
      setDeckRevealed(data.deckRevealed ?? true);
      setSessionBrief(data.brief || null);
      setSessionPhase(data.phase || "esplorazione_ideazione");
      setSessionMode(data.mode || null);
      setSessionModeAskAttempts(data.modeAskAttempts || 0);
      setPendingCheckpoint(data.pendingCheckpoint || false);
      setSessionTeacherStage(data.teacherStage || "osserva");
      setMindmapObjectiveId(data.mindmapObjectiveId || null);
      setScenarioSplits(Array.isArray(data.scenarioSplits) ? data.scenarioSplits : []);
      setScenarioBranches(Array.isArray(data.scenarioBranches) ? data.scenarioBranches : []);
      setMatrixAxisLabels((data.matrixAxisLabels && typeof data.matrixAxisLabels === "object")
        ? { a: data.matrixAxisLabels.a || t('canvas.matrix.axisA'), b: data.matrixAxisLabels.b || t('canvas.matrix.axisB') }
        : { a: t('canvas.matrix.axisA'), b: t('canvas.matrix.axisB') });
      { const chips = (data.evidenceChips && typeof data.evidenceChips === "object") ? data.evidenceChips : {}; setEvidenceChips(chips); seedEvidenceKeys(chips); }
      setSessionMessages(Array.isArray(data.messages) ? data.messages : []);
      sessionFileHandleRef.current = handle;
      const base = stripSessionExt(name);
      if (base) { setSessionName(base); setTitleEdited(true); } // opened file's title wins
      setSaveFeedback(t('canvas.saveFeedback.opened'));
    } catch (e) {
      console.error("open session file failed:", e);
      setSaveFeedback(t('canvas.saveFeedback.openError'));
    }
  };


  // `update` is either a plain string (legacy label-only update, from
  // PostItNode/ResizableTextNode's inline text edit) or a partial data
  // object (shape/color changes from the note's right-click menu below).
  const onNodeUpdate = useCallback((nodeId, update) => {
    const patch = typeof update === "string" ? { label: update } : update;
    setNodes(prevNodes => prevNodes.map(node => (
        node.id === nodeId ? { ...node, data: { ...node.data, ...patch } } : node
    )));
}, [setNodes]);










  // ---- Guided template starter canvases ----
  // Applies a template's guiding notes onto the canvas. Keeps the card deck;
  // replaces existing note nodes.
  const [pendingTemplate, setPendingTemplate] = useState(null);

  // File menu's "Nuova Dashboard" — full reset, confirmed first (destructive).
  const [pendingNewDashboard, setPendingNewDashboard] = useState(false);

  // Requested but not-yet-confirmed sessionMode change. Holds the target
  // mode string, or null when no switch is pending.
  const [pendingModeSwitch, setPendingModeSwitch] = useState(null);

  // Id of the auto-created "Obiettivo" post-it in mappatura mode. null until
  // the first switch into mappatura; persisted so a reload doesn't duplicate it.
  const [mindmapObjectiveId, setMindmapObjectiveId] = useState(null);

  // Explicit delete from a note's right-click menu — bypasses onNodesChange's
  // `locked` guard on purpose: that guard only exists to stop an accidental
  // keyboard/selection delete of the mappatura Obiettivo note while it's
  // still relevant, not to make it permanently undeletable once the user
  // deliberately asks to remove it. Also drops any edges attached to it and
  // clears mindmapObjectiveId so re-entering mappatura later creates a fresh
  // one instead of silently no-op'ing against a note that no longer exists.
  const onNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setMindmapObjectiveId((prev) => (prev === nodeId ? null : prev));
  }, [setNodes, setEdges]);

  // scenari_futuri mode: branching timeline state (see ScenarioTimeline.jsx).
  // A split marks a fork point (afterNodeId references "root" or a branch
  // id); each split fans out into one or more branches. `pending: true`
  // marks a mentor-suggested split awaiting accept/dismiss.
  const [scenarioSplits, setScenarioSplits] = useState([]); // [{id, afterNodeId, pending}]
  const [scenarioBranches, setScenarioBranches] = useState([]); // [{id, parentSplitId, label, createdBy, matrixPos}]
  // Uncertainty-matrix axis names (ScenarioMatrix.jsx, Task 3 of this plan) —
  // free text the user edits inline; defaults cover sessions saved before
  // this field existed.
  const [matrixAxisLabels, setMatrixAxisLabels] = useState({ a: t('canvas.matrix.axisA'), b: t('canvas.matrix.axisB') });

  // Plain-text description of labeled+positioned scenario branches, folded
  // into the mentor brief's `direzione` field client-side (scenari_futuri
  // mode only) — see onBrief below. Frontend-only; scenarioBranches is never
  // sent to the backend.
  const describeScenarioBranches = useCallback(() => {
    const described = scenarioBranches.filter((b) => b.label && b.matrixPos);
    if (described.length === 0) return "";
    // ScenarioMatrix.jsx binds axisLabels.b to the horizontal (x) axis and
    // axisLabels.a to the vertical (y) axis (its --x AxisLabelInput reads
    // axisLabels.b, its --y one reads axisLabels.a) — match that pairing
    // here so the text doesn't mislabel a branch's position.
    return described.map((b) =>
      `Scenario ${b.label} — posizionato su ${matrixAxisLabels.b}: ${Math.round(b.matrixPos.x * 100)}%, ${matrixAxisLabels.a}: ${Math.round(b.matrixPos.y * 100)}%.`
    ).join("\n");
  }, [scenarioBranches, matrixAxisLabels]);

  const requestModeSwitch = useCallback((newMode) => {
    if (newMode === (sessionMode || 'tradizionale')) return; // no-op, same mode re-selected
    setPendingModeSwitch(newMode);
  }, [sessionMode]);

  // Resets phase/brief/mode-negotiation state and applies the new mode.
  // Cards/notes/edges are NOT deleted — each mode's layout task (this plan's
  // Task 2, and 2026-08-17-scenario-timeline-matrix.md) re-renders them
  // under the new mode's rules on the next render.
  const applyModeSwitch = useCallback((newMode) => {
    setSessionMode(newMode);
    setSessionPhase("esplorazione_ideazione");
    setSessionBrief(null);
    setSessionModeAskAttempts(0);
    setPendingCheckpoint(false);
    setSessionTeacherStage("osserva");
    setPendingModeSwitch(null);
    if (newMode === 'mappatura' && !mindmapObjectiveId) {
      const objId = `mindmap-objective-${Date.now()}`;
      const center = {
        x: AREA_BOUNDS.brainstorm.x + AREA_BOUNDS.brainstorm.w / 2 - 100,
        y: AREA_BOUNDS.brainstorm.y + AREA_BOUNDS.brainstorm.h / 2 - 70,
      };
      // No `purpose` variable exists in this component (it's local state
      // inside Chatbot.jsx) — reuse the same session-objective fallback
      // chain already used for the title elsewhere in this file (e.g. the
      // session title button and openConceptBrief).
      const objectiveLabel = (sessionBrief && sessionBrief.obiettivo)
        || (sessionName && sessionName !== 'default_name' ? sessionName : null)
        || t('canvas.mindmap.objectiveTag');
      setNodes((nds) => [...nds, {
        id: objId,
        type: "postItNode",
        position: center,
        data: {
          label: objectiveLabel,
          group: "note",
          id: objId,
          onNodeUpdate,
          onNodeDelete,
          w: 220,
          h: 140,
          bg: "#FFF7E6",
          radius: "16px",
          tag: t('canvas.mindmap.objectiveTag'),
          tagColor: "#C9971E",
          locked: true,
        },
      }]);
      setMindmapObjectiveId(objId);
    }
  }, [mindmapObjectiveId, sessionBrief, sessionName, onNodeUpdate, onNodeDelete, setNodes, t]);

  // User clicks "+biforca" on the root or on a branch node: adds a new fork
  // point right there, seeded with one blank (user-labelled) branch. Only a
  // level-1 branch (forked from root) gets a fresh auto-assigned color —
  // any deeper branch inherits its PARENT branch's current color instead
  // (which is also whatever a manual right-click recolor last set it to,
  // so a fork created after a manual recolor picks that color up too,
  // same as every sibling already cascaded by updateBranchStyle).
  const addScenarioSplit = useCallback((afterNodeId) => {
    const splitId = `split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setScenarioSplits((prev) => [...prev, { id: splitId, afterNodeId, pending: false }]);
    const branchId = `${splitId}-b0`;
    setScenarioBranches((prev) => {
      const parentColor = afterNodeId === "root" ? null : prev.find((b) => b.id === afterNodeId)?.color;
      const color = parentColor || scenarioBranchColor(prev.length);
      return [...prev, { id: branchId, parentSplitId: splitId, label: "", createdBy: "user", matrixPos: null, color }];
    });
  }, []);

  // Text edits to a branch label — the split-creation helpers above always
  // seed a blank ("") or mentor-authored label; this is how either gets
  // renamed from ScenarioTimeline.jsx's inline input.
  const renameScenarioBranch = useCallback((branchId, label) => {
    setScenarioBranches((prev) => prev.map((b) => (b.id === branchId ? { ...b, label } : b)));
  }, []);

  // Chip drag on the uncertainty matrix (ScenarioMatrix.jsx) — only touches
  // matrixPos, leaving the branch's timeline label/parentSplitId untouched.
  const updateBranchMatrixPos = useCallback((branchId, pos) => {
    setScenarioBranches((prev) => prev.map((b) => (b.id === branchId ? { ...b, matrixPos: pos } : b)));
  }, []);

  // Right-click color/stroke customization on a scenario branch (path
  // highlighting) — patches whichever of {color, strokeStyle} is passed.
  // Cascades to every descendant branch too (the whole sub-path adopts the
  // new look), but that's only a starting point: re-picking any individual
  // descendant afterward overrides it there without touching the rest.
  const updateBranchStyle = useCallback((branchId, patch) => {
    const splitsByAfter = {};
    scenarioSplits.forEach((s) => {
      (splitsByAfter[s.afterNodeId] = splitsByAfter[s.afterNodeId] || []).push(s);
    });
    const branchesBySplit = {};
    scenarioBranches.forEach((b) => {
      (branchesBySplit[b.parentSplitId] = branchesBySplit[b.parentSplitId] || []).push(b);
    });
    const affected = new Set([branchId]);
    const stack = [branchId];
    while (stack.length > 0) {
      const id = stack.pop();
      (splitsByAfter[id] || []).forEach((split) => {
        (branchesBySplit[split.id] || []).forEach((b) => {
          if (!affected.has(b.id)) {
            affected.add(b.id);
            stack.push(b.id);
          }
        });
      });
    }
    setScenarioBranches((prev) => prev.map((b) => (affected.has(b.id) ? { ...b, ...patch } : b)));
  }, [scenarioSplits, scenarioBranches]);

  // Right-click "Elimina scenario" — removes this branch and its whole
  // sub-tree (every split forked from it, directly or transitively, and
  // every branch those splits produced), same descendant-walk as
  // updateBranchStyle above. The branch's own parent split is left as-is:
  // any sibling branches stay put, and if this was the split's only
  // branch, the now-childless split just renders nothing (buildLayout only
  // produces rows from branchesBySplit) — harmless leftover state, not
  // worth a second pass to prune.
  const deleteScenarioBranch = useCallback((branchId) => {
    const splitsByAfter = {};
    scenarioSplits.forEach((s) => {
      (splitsByAfter[s.afterNodeId] = splitsByAfter[s.afterNodeId] || []).push(s);
    });
    const branchesBySplit = {};
    scenarioBranches.forEach((b) => {
      (branchesBySplit[b.parentSplitId] = branchesBySplit[b.parentSplitId] || []).push(b);
    });
    const affectedBranches = new Set([branchId]);
    const affectedSplits = new Set();
    const stack = [branchId];
    while (stack.length > 0) {
      const id = stack.pop();
      (splitsByAfter[id] || []).forEach((split) => {
        affectedSplits.add(split.id);
        (branchesBySplit[split.id] || []).forEach((b) => {
          if (!affectedBranches.has(b.id)) {
            affectedBranches.add(b.id);
            stack.push(b.id);
          }
        });
      });
    }
    setScenarioBranches((prev) => prev.filter((b) => !affectedBranches.has(b.id)));
    setScenarioSplits((prev) => prev.filter((s) => !affectedSplits.has(s.id)));
  }, [scenarioSplits, scenarioBranches]);

  // Mentor proposed one or more splits (scenari_futuri mode's suggested_splits
  // field, Task 1) — land them as pending so the user can accept/dismiss each
  // one from the timeline instead of them silently becoming canon.
  const handleSplitAction = useCallback((suggestedSplits) => {
    let addedCount = 0;
    let nextColorIndex = scenarioBranches.length;
    // Only a level-1 split (afterNodeId === "root") gets fresh auto-colors
    // for its branches — a split forked from an existing branch inherits
    // that branch's current color instead (same rule as addScenarioSplit's
    // user-driven fork). colorById seeds from the live scenarioBranches and
    // also picks up branches created earlier in THIS same forEach, so a
    // second split in the same mentor turn that forks off the first split's
    // new branch still resolves the right color.
    const colorById = new Map(scenarioBranches.map((b) => [b.id, b.color]));
    suggestedSplits.forEach((s) => {
      // The mentor now gets the real branch-id tree (Chatbot.jsx's
      // scenarioTree prop, see scenarioTreeSummary.js) so it can target an
      // existing branch instead of always forking from root — but a
      // hallucinated afterNodeId is still possible on a bad turn, and would
      // create a split unreachable from ScenarioTimeline's tree-walk
      // (buildLayout) while still living in the flat scenarioBranches/
      // scenarioSplits arrays — an undismissable ghost chip in
      // ScenarioMatrix. Drop it here, at creation time, instead.
      const validAfterNode = s.afterNodeId === "root" || scenarioBranches.some((b) => b.id === s.afterNodeId);
      if (!validAfterNode) return;
      const splitId = `split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setScenarioSplits((prev) => [...prev, { id: splitId, afterNodeId: s.afterNodeId, pending: true }]);
      const parentColor = s.afterNodeId === "root" ? null : colorById.get(s.afterNodeId);
      const newBranches = s.branches.map((label, i) => ({
        // Parent split's id + branch index — inherently unique per split
        // (splitId already carries its own Date.now()+random suffix), so two
        // splits landing in the same forEach tick (up to 2 per mentor turn,
        // see valid_splits[:2] / .slice(0, 2) on the backend/slmMentor side)
        // can never collide the way plain `branch-${Date.now()}-${i}` did.
        id: `${splitId}-b${i}`,
        parentSplitId: splitId,
        label,
        createdBy: "mentor",
        matrixPos: null,
        color: parentColor || scenarioBranchColor(nextColorIndex++),
      }));
      newBranches.forEach((b) => colorById.set(b.id, b.color));
      setScenarioBranches((prev) => [...prev, ...newBranches]);
      addedCount += 1;
    });
    if (addedCount > 0) {
      // Same live-measured tree size handleCardAction uses for placement —
      // may be one render-cycle stale relative to the branch(es) just
      // added above (see scenarioTreeCameraBounds's own comment for why
      // that's fine here).
      startScenarioBeat(
        addedCount > 1
          ? t('canvas.scenarioBeat.splitSuggestedMultiple')
          : t('canvas.scenarioBeat.splitSuggestedSingle'),
        scenarioTreeCameraBounds(scenarioTreeSize)
      );
    }
  }, [scenarioBranches, scenarioTreeSize, startScenarioBeat, t]);

  const acceptScenarioSplit = useCallback((splitId) => {
    setScenarioSplits((prev) => prev.map((s) => (s.id === splitId ? { ...s, pending: false } : s)));
  }, []);

  const dismissScenarioSplit = useCallback((splitId) => {
    setScenarioSplits((prev) => prev.filter((s) => s.id !== splitId));
    setScenarioBranches((prev) => prev.filter((b) => b.parentSplitId !== splitId));
  }, []);

  const applyTemplate = useCallback((tpl) => {
    const built = tpl.build(lang);
    const templateNodes = built.nodes.map((n) => ({ ...n, data: { ...n.data, onNodeUpdate, onNodeDelete } }));
    const deck = nodes.filter((n) => n.type === 'customCardNode');
    setNodes([...deck, ...templateNodes]);
    setEdges([]);
    setSessionName(built.name);
    setTemplateOpen(false);
  }, [nodes, onNodeUpdate, onNodeDelete, setNodes, setEdges, lang]);

  // Load a template — asks first (in-app dialog, not window.confirm) only
  // when it would actually discard existing notes.
  const loadTemplate = useCallback((tpl) => {
    const hasNotes = nodes.some((n) => n.type !== 'customCardNode');
    if (hasNotes) {
      setPendingTemplate(tpl);
      return;
    }
    applyTemplate(tpl);
  }, [nodes, applyTemplate]);

  // First run: show the welcome coachmarks (which can open the template picker).
  useEffect(() => {
    try {
      if (!localStorage.getItem('sg_coachmarks_seen')) {
        setCoachOpen(true);
        localStorage.setItem('sg_coachmarks_seen', '1');
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Mentor open by default only for Pro (it's the live chat); Free users start
  // with a clean canvas and open the upsell from the ✦ button.
  useEffect(() => {
    setIsChatbotVisible(isPro);
  }, [isPro]);

  // Proactive mentor nudge once a few cards are on the canvas (once per session).
  useEffect(() => {
    if (!nudgedRef.current && usedCards.length >= 3) {
      nudgedRef.current = true;
      setNudgeOpen(true);
    }
  }, [usedCards.length]);

  // Compile a 1-page concept brief from the current canvas state.
  const openConceptBrief = useCallback(() => {
    const placeholders = ['new sticky note', 'edit text...'];
    const cards = usedCards.map((c) => ({ label: c.label, group: c.group }));
    const notes = nodes
      .filter((n) => n.type === 'postItNode' || n.type === 'resizableText')
      .map((n) => ({ label: (n.data && n.data.label ? n.data.label : '').trim(), tag: n.data && n.data.tag }))
      .filter((n) => n.label && !placeholders.includes(n.label.toLowerCase()));
    const title = (sessionBrief && sessionBrief.obiettivo)
      || (sessionName && sessionName !== 'default_name' ? sessionName : t('canvas.conceptBrief.defaultTitle'));
    const bs = brainstorm.filter((e) => e.card || (e.html && e.html.replace(/<[^>]*>/g, '').trim()));
    setBriefData({
      title, summary: sessionBrief?.sintesi_processo || '', cards, notes, brainstorm: bs,
      cantiere: sessionBrief?.cantiere || EMPTY_CANTIERE,
      fondamenta: sessionBrief?.fondamenta || EMPTY_FONDAMENTA,
      direzione: sessionBrief?.direzione || '',
      evidenzeChiave: sessionBrief?.evidenze_chiave || [],
      carteCollegate: sessionBrief?.carte_collegate || [],
      decisions: sessionBrief?.decisions || [],
    });
    setBriefOpen(true);
  }, [usedCards, nodes, sessionName, sessionBrief, brainstorm, t]);

  // Manual "rigenera sommario" — an incremental re-sync (same summarize pass
  // the mentor runs after every turn), seeded from the session's existing
  // brief instead of wiped, over a BOUNDED recent chat window instead of the
  // entire, unboundedly-growing transcript. Sending the full transcript in
  // one shot used to be the actual cause of this endpoint reliably failing
  // on any non-trivial session (token/context limits) — the window here
  // matches the short-term-memory convention Chatbot.jsx already uses for
  // every other mentor call (.slice(-6) for the local paths). Always goes
  // through the cloud backend regardless of the chat's currently selected
  // tier: this is a deliberate, low-frequency action where response quality
  // matters more than avoiding a token-quota hit, and it keeps the call
  // site simple (Chatbot.jsx owns local/native tier state, not this
  // component).
  const regenerateSessionSummary = useCallback(async () => {
    const { user_label: userLabel, mentor_label: mentorLabel } = vocab(lang);
    const chat = sessionMessages
      .slice(-6)
      .map((m) => `${m.sender === 'user' ? userLabel : mentorLabel}: ${m.text || m.message || ''}`)
      .join(' | ');
    // v1: no Gemini backend — this manual "regenerate summary" runs on the
    // local engine (same summarize pass runEngineMentor does per turn).
    const data = await engineResummarize({
      chat,
      usedCards: usedCards.map((c) => ({ label: c.label })),
      prevBrief: {
        obiettivo: sessionBrief?.obiettivo || '',
        sintesi_processo: sessionBrief?.sintesi_processo || '',
        cantiere: sessionBrief?.cantiere || EMPTY_CANTIERE,
        fondamenta: sessionBrief?.fondamenta || EMPTY_FONDAMENTA,
        decisions: sessionBrief?.decisions || [],
      },
      currentPhase: sessionPhase,
      lang,
    });
    if (!data.obiettivo && !data.sintesi) {
      throw new Error('Rigenerazione fallita');
    }
    setSessionBrief((prev) => ({
      ...(prev || {}),
      obiettivo: data.obiettivo || prev?.obiettivo || '',
      sintesi_processo: data.sintesi || prev?.sintesi_processo || '',
      cantiere: data.cantiere || prev?.cantiere || EMPTY_CANTIERE,
      fondamenta: data.fondamenta || prev?.fondamenta || EMPTY_FONDAMENTA,
      decisions: data.decisions || prev?.decisions || [],
    }));
    // Also patch the already-open brief panel's own snapshot (briefData is
    // captured once at openConceptBrief time, not derived live from
    // sessionBrief) so a regenerate while the panel is open is visible
    // immediately instead of only on the next open.
    setBriefData((prev) => (prev ? {
      ...prev,
      title: data.obiettivo || prev.title,
      summary: data.sintesi || prev.summary,
      cantiere: data.cantiere || prev.cantiere,
      fondamenta: data.fondamenta || prev.fondamenta,
      decisions: data.decisions || prev.decisions,
    } : prev));
    return data;
  }, [sessionMessages, sessionId, usedCards, currentUser, sessionBrief, sessionPhase, lang]);

  const exportBrainstormPng = useCallback(async () => {
    const { toBlob } = await import('html-to-image');
    const el = document.querySelector('.zone-layer');
    if (!el) return;
    try {
      // Blob + object URL, not a data: URL — a scenario tree can rasterize
      // into a large image (pixelRatio 2), and huge data URIs are fragile
      // for anchor downloads in some browsers. This is purely a client-side
      // file save; nothing is ever sent to the backend.
      const blob = await toBlob(el, { backgroundColor: undefined, pixelRatio: 2 });
      if (!blob) throw new Error('toBlob returned null');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sessionName || 'sessione'}-brainstorming-${sessionMode || 'tradizionale'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PNG export failed:', e);
      setSaveFeedback(t('canvas.exportError'));
    }
  }, [sessionName, sessionMode, t]);

  // Per-area export (the small button in each area's own top-right corner,
  // see ZoneLayer.js). Captures .canvas — the common ancestor of both
  // .zone-layer AND react-flow — so the exported image actually shows the
  // area's real content (cards included), unlike exportBrainstormPng (which
  // only ever captures .zone-layer: decorative panels/labels, no card/note
  // content, since react-flow renders those in a separate DOM subtree).
  // .canvas is viewport-clipped (overflow:hidden) and shows whatever's
  // currently on-screen at the current pan/zoom; cropping to the target
  // area's own on-screen rect is exactly why this is safe regardless of
  // zoom level — no viewport navigation needed.
  const cropAndDownload = useCallback(async (fullCanvas, container, areaEl, filenameSuffix) => {
    const containerRect = container.getBoundingClientRect();
    const areaRect = areaEl.getBoundingClientRect();
    const scaleX = fullCanvas.width / containerRect.width;
    const scaleY = fullCanvas.height / containerRect.height;
    // Clip to the container's own bounds — the area panel can run past the
    // visible viewport (e.g. an extended-width brainstorm region, or any
    // area currently outside the visible pan/zoom), and a negative or
    // oversized crop rect would throw or produce a blank image.
    const cropX = Math.max(0, (areaRect.left - containerRect.left) * scaleX);
    const cropY = Math.max(0, (areaRect.top - containerRect.top) * scaleY);
    const cropRight = Math.min(fullCanvas.width, (areaRect.right - containerRect.left) * scaleX);
    const cropBottom = Math.min(fullCanvas.height, (areaRect.bottom - containerRect.top) * scaleY);
    const cropW = cropRight - cropX;
    const cropH = cropBottom - cropY;
    if (cropW <= 0 || cropH <= 0) {
      setSaveFeedback(t('canvas.exportAreaError'));
      return;
    }
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropW;
    cropCanvas.height = cropH;
    cropCanvas.getContext('2d').drawImage(fullCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    const blob = await new Promise((resolve) => cropCanvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('toBlob returned null');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionName || 'sessione'}-${filenameSuffix}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [sessionName, t]);

  const exportAreaPng = useCallback(async (areaKey, filenameSuffix) => {
    const { toCanvas } = await import('html-to-image');
    const areaEl = document.querySelector(`.area-region--${areaKey}`);
    if (!areaEl) return;
    const pixelRatio = 2;
    // Card back-images are hosted on Firebase Storage without a CORS header
    // permitting this origin — html-to-image can't embed them as a data URI
    // and rejects the WHOLE capture on the first failed image rather than
    // degrading gracefully (confirmed live: imagePlaceholder alone doesn't
    // save it — the failures come from CSS background-image, not just <img
    // src>, which that option doesn't cover).
    const TRANSPARENT_PX = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';
    try {
      const container = document.querySelector('.canvas');
      const fullCanvas = await toCanvas(container, { backgroundColor: undefined, pixelRatio, imagePlaceholder: TRANSPARENT_PX });
      await cropAndDownload(fullCanvas, container, areaEl, filenameSuffix);
    } catch (e) {
      // Fall back to the same capture root exportBrainstormPng already
      // relies on successfully today (.zone-layer has no <img>/
      // background-image content at all, so it can't hit this failure) —
      // graceful degradation to decorative-only content instead of a hard
      // failure, not a regression: the whole-canvas export has always had
      // this same card-image limitation.
      console.warn('Area PNG export: .canvas capture failed (likely cross-origin card images), falling back to decorative-only:', e);
      try {
        const fallbackContainer = document.querySelector('.zone-layer');
        const fallbackCanvas = await toCanvas(fallbackContainer, { backgroundColor: undefined, pixelRatio });
        await cropAndDownload(fallbackCanvas, fallbackContainer, areaEl, filenameSuffix);
      } catch (e2) {
        console.error('Area PNG export failed:', e2);
        setSaveFeedback(t('canvas.exportError'));
      }
    }
  }, [cropAndDownload, t]);

  const buildLocalSnapshot = useCallback(() => ({
    sessionId,
    nodes: sanitizeNodesForStorage(nodes),
    edges,
    brainstorm,
    deckRevealed,
    brief: sessionBrief,
    phase: sessionPhase,
    teacherStage: sessionTeacherStage,
    mode: sessionMode,
    modeAskAttempts: sessionModeAskAttempts,
    pendingCheckpoint,
    mindmapObjectiveId,
    scenarioSplits,
    scenarioBranches,
    matrixAxisLabels,
    evidenceChips,
    messages: sessionMessages,
    savedAt: Date.now(),
  }), [sessionId, nodes, edges, brainstorm, deckRevealed, sessionBrief, sessionPhase, sessionMode, sessionModeAskAttempts, pendingCheckpoint, sessionTeacherStage, mindmapObjectiveId, scenarioSplits, scenarioBranches, matrixAxisLabels, evidenceChips, sessionMessages]);

  const persistLocalDraft = useCallback(() => {
    if (!userId) return;
    try {
      const snap = buildLocalSnapshot();
      // A pristine/untouched canvas is not a draft. Don't write it — and
      // clear any stale one a prior version may have left — so the restore
      // prompt never fires for a canvas the user never worked on.
      if (!draftHasMeaningfulContent(snap)) {
        localStorage.removeItem(draftKeyFor(userId));
        return;
      }
      localStorage.setItem(draftKeyFor(userId), JSON.stringify(snap));
    } catch (e) {
      console.warn('local draft save failed (non-fatal):', e);
    }
  }, [userId, buildLocalSnapshot]);


  //------------------------
  // END LOAD and Save Session

  // Debounced local-draft autosave: write ~2-3s after the last relevant
  // state change, plus a synchronous final flush on tab close/reload, plus
  // registration so AuthContext's forced-logout path can trigger a save too.
  useEffect(() => {
    const t = setTimeout(persistLocalDraft, 2500);
    return () => clearTimeout(t);
  }, [persistLocalDraft]);

  useEffect(() => {
    const flush = () => persistLocalDraft();
    window.addEventListener('beforeunload', flush);
    registerDraftSaver(persistLocalDraft);
    return () => {
      window.removeEventListener('beforeunload', flush);
      unregisterDraftSaver();
    };
  }, [persistLocalDraft]);

  // Offer to restore an unexpired local draft on first load. One slot per
  // user (not per session — sessionId is regenerated every reload), so the
  // draft's own embedded sessionId is re-adopted on restore. Detection only
  // here; the in-app dialog below decides restore vs. discard.
  const [pendingDraft, setPendingDraft] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let raw;
    try {
      raw = localStorage.getItem(draftKeyFor(userId));
    } catch (e) {
      return;
    }
    if (!raw) return;
    let draft;
    try {
      draft = JSON.parse(raw);
    } catch (e) {
      localStorage.removeItem(draftKeyFor(userId));
      return;
    }
    if (!draft || isDraftExpired(draft.savedAt) || !draftHasMeaningfulContent(draft)) {
      // Expired, or a pristine canvas an older build persisted — drop it
      // silently instead of prompting to "restore" nothing.
      try { localStorage.removeItem(draftKeyFor(userId)); } catch (e) { /* ignore */ }
      return;
    }
    setPendingDraft(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const resolveDraftPrompt = useCallback((restore) => {
    if (restore && pendingDraft) {
      try {
        if (!Array.isArray(pendingDraft.nodes) || !Array.isArray(pendingDraft.edges)) {
          throw new Error('draft.nodes/edges is not an array');
        }
        const rehydratedNodes = pendingDraft.nodes.map((node) => {
          if (node.type === 'customCardNode') {
            return { ...node, data: { ...node.data, onCardNodeUpdate, onShowInfo: (d) => setCardDetail(d) } };
          }
          return { ...node, data: { ...node.data, onNodeUpdate, onNodeDelete } };
        });
        setSessionId(pendingDraft.sessionId);
        setNodes(deck ? relocalizeCardNodes(rehydratedNodes, deck.cards) : rehydratedNodes);
        setEdges(pendingDraft.edges);
        setBrainstorm(Array.isArray(pendingDraft.brainstorm) ? pendingDraft.brainstorm : []);
        setDeckRevealed(pendingDraft.deckRevealed ?? true);
        setSessionBrief(pendingDraft.brief || null);
        setSessionPhase(pendingDraft.phase || "esplorazione_ideazione");
        setSessionMode(pendingDraft.mode || null);
        setSessionModeAskAttempts(pendingDraft.modeAskAttempts || 0);
        setPendingCheckpoint(pendingDraft.pendingCheckpoint || false);
        setSessionTeacherStage(pendingDraft.teacherStage || "osserva");
        setMindmapObjectiveId(pendingDraft.mindmapObjectiveId || null);
        setScenarioSplits(Array.isArray(pendingDraft.scenarioSplits) ? pendingDraft.scenarioSplits : []);
        setScenarioBranches(Array.isArray(pendingDraft.scenarioBranches) ? pendingDraft.scenarioBranches : []);
        setMatrixAxisLabels((pendingDraft.matrixAxisLabels && typeof pendingDraft.matrixAxisLabels === "object")
          ? { a: pendingDraft.matrixAxisLabels.a || t('canvas.matrix.axisA'), b: pendingDraft.matrixAxisLabels.b || t('canvas.matrix.axisB') }
          : { a: t('canvas.matrix.axisA'), b: t('canvas.matrix.axisB') });
        { const chips = (pendingDraft.evidenceChips && typeof pendingDraft.evidenceChips === "object") ? pendingDraft.evidenceChips : {}; setEvidenceChips(chips); seedEvidenceKeys(chips); }
        setSessionMessages(Array.isArray(pendingDraft.messages) ? pendingDraft.messages : []);
      } catch (e) {
        console.warn('draft restore failed (non-fatal):', e);
      }
    }
    // On discard, the canvas was NOT loaded from the draft — it stays the
    // pristine mount state, which draftHasMeaningfulContent() rejects, so the
    // autosave won't re-persist it and the prompt won't come back.
    try { localStorage.removeItem(draftKeyFor(userId)); } catch (e) { /* ignore */ }
    setPendingDraft(null);
  }, [pendingDraft, userId, onCardNodeUpdate, onNodeUpdate, onNodeDelete, setNodes, setEdges, t, deck]);

  // Startup: auto-deal the deck face-up (instead of waiting for a manual
  // DeckPileButton click) and zoom out to frame the whole dashboard. Waits
  // for the unsaved-draft prompt (above) to resolve first — restoring a
  // draft can set deckRevealed itself, and firing this first would animate
  // a reveal that's about to be wiped out from under it. Guarded to run
  // once per mount (deckRevealed flips true either way right after).
  const autoRevealedRef = useRef(false);
  // useReactFlow() hands back a fresh fitBounds reference on every render, so
  // putting it (or revealDeck, which depends on it transitively via deck) in
  // this effect's dependency array made it re-run on nearly every render —
  // each re-run's cleanup cancelled the pending setTimeout, and the
  // autoRevealedRef guard then blocked ever rescheduling it, so the reveal
  // silently never fired. Latest-ref pattern: call the current function via
  // a ref at fire time, but only actually re-run the effect (arm the timer)
  // when the semantically meaningful gating values change.
  const revealDeckRef = useRef(revealDeck);
  revealDeckRef.current = revealDeck;
  const fitBoundsRef = useRef(fitBoundsOnCardAction);
  fitBoundsRef.current = fitBoundsOnCardAction;
  useEffect(() => {
    if (autoRevealedRef.current) return;
    if (!deck || pendingDraft) return;
    autoRevealedRef.current = true;
    if (deckRevealed) return; // resumed session already had it revealed
    const t = setTimeout(() => {
      revealDeckRef.current();
      fitBoundsRef.current(getDashboardBounds(), { padding: 0.08, duration: 900 });
    }, 300);
    return () => clearTimeout(t);
  }, [deck, deckRevealed, pendingDraft]);

  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        type: currentEdgeType.type,
        style: { strokeDasharray: getStrokeDashArray(currentEdgeStyle.style) },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: "#888",
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, currentEdgeType, currentEdgeStyle.style]
  );

  const getStrokeDashArray = (style) => {
    switch (style) {
      case "dashed":
        return "5, 5";
      case "dotted":
        return "1, 5";
      default:
        return "";
    }
  };

  const toggleEdgeType = () => {
    const currentIndex = edgeTypes.findIndex(
      (et) => et.type === currentEdgeType.type
    );
    const nextIndex = (currentIndex + 1) % edgeTypes.length;
    setCurrentEdgeType(edgeTypes[nextIndex]);
  };

  const toggleEdgeStyle = () => {
    const currentIndex = edgeStyles.findIndex(
      (es) => es.style === currentEdgeStyle.style
    );
    const nextIndex = (currentIndex + 1) % edgeStyles.length;
    setCurrentEdgeStyle(edgeStyles[nextIndex]);
  };

  const updateSelectedEdgeStyle = () => {
    if (selectedEdge) {
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === selectedEdge.id
            ? {
                ...edge,
                style: {
                  strokeDasharray: getStrokeDashArray(currentEdgeStyle.style),
                },
                type: currentEdgeType.type,
              }
            : edge
        )
      );
      setSelectedEdge(null); // Reset selected edge after updating style
    }
  };


  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
  }, []);

  const toggleGroup = useCallback((group) => {
    setActiveGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  }, []);

  const allGroupLabels = useMemo(
    () => [...(deck?.groups.map((g) => g.id) || []), "note"],
    [deck]
  );

  const toggleAllGroups = useCallback(() => {
    setActiveGroups((prev) => (prev.length === allGroupLabels.length ? [] : allGroupLabels));
  }, [allGroupLabels]);

  // console.log("Nodes data structure before filtering:", nodes);
  // console.log("Is nodes an array?", Array.isArray(nodes));

  const filteredNodes = nodes.filter((node) => {
    // Sticky notes (every shape) hide/show together via the "note" chip.
    if (node.type === "postItNode") return activeGroups.includes("note");
    // Text boxes / other non-card nodes are never filtered.
    if (node.type !== "customCardNode") return true;
    return (
      node.data.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
      activeGroups.includes(node.data.group)
    );
  });

  const addStickyNote = useCallback((shape = "rect", color = "#FFE066") => {
    const preset = STICKY_SHAPES[shape] || STICKY_SHAPES.rect;
    // Spawn near the top toolbar in the current view, so it's easy to grab + drag.
    let position = { x: 250, y: 150 };
    try {
      if (reactFlowInstance && reactFlowInstance.project) {
        position = reactFlowInstance.project({ x: 360, y: 150 });
      }
    } catch (e) { /* keep fallback */ }
    const newNoteId = `note-${Date.now()}`;
    const newNote = {
      id: newNoteId,
      type: "postItNode",
      position,
      data: {
        label: t('canvas.postIt.newNoteDefault'),
        group: "note",
        id: newNoteId,
        onNodeUpdate: onNodeUpdate,
        onNodeDelete,
        bg: color,
        w: preset.w,
        h: preset.h,
        radius: preset.radius,
        shape,
      },
    };

    setNodes((currentNodes) => [...currentNodes, newNote]);
    setStickyAnchor(null);
  }, [setNodes, onNodeUpdate, onNodeDelete, reactFlowInstance, t]);

  // evidenceKeysRef is a derived in-memory dedup Set rebuilt from
  // evidenceChips (declared earlier, alongside the other session state) on
  // every session load.
  const evidenceKeysRef = useRef(new Set());
  // Per-phase stacking counter for mentor-authored notes — unrelated to
  // evidence chips, still used by commitMagicNote's note placement.
  const mentorNoteCountRef = useRef({});

  const seedEvidenceKeys = useCallback((chips) => {
    const keys = new Set();
    Object.entries(chips || {}).forEach(([phase, texts]) => {
      (texts || []).forEach((t) => keys.add(`${phase}::${t.toLowerCase()}`));
    });
    evidenceKeysRef.current = keys;
  }, []);

  // File menu's "Nuova Dashboard" — wipes the canvas back to a pristine
  // fresh session (new id, face-down deck, no notes/edges/mode/brief/
  // scenario state), same shape as a genuine first-ever visit. Drops the
  // autosaved draft too, so the "unsaved session found" prompt doesn't
  // immediately re-offer the content just discarded.
  const startNewDashboard = useCallback(() => {
    if (!deck) return;
    setSessionId(uuidv4());
    setSessionName('default_name');
    setTitleEdited(false);
    setNodes(generateInitialNodes(deck.cards, role, isPro, (d) => setCardDetail(d), onCardNodeUpdate));
    setEdges([]);
    setBrainstorm([]);
    setDeckRevealed(false);
    setSessionBrief(null);
    setSessionPhase("esplorazione_ideazione");
    setSessionMode(null);
    setSessionModeAskAttempts(0);
    setPendingCheckpoint(false);
    setSessionTeacherStage("osserva");
    setMindmapObjectiveId(null);
    setScenarioSplits([]);
    setScenarioBranches([]);
    setMatrixAxisLabels({ a: t('canvas.matrix.axisA'), b: t('canvas.matrix.axisB') });
    setEvidenceChips({});
    seedEvidenceKeys({});
    setSessionMessages([]);
    autoRevealedRef.current = false; // let the startup deal + zoom-to-fit replay
    try { localStorage.removeItem(draftKeyFor(userId)); } catch (e) { /* ignore */ }
    setPendingNewDashboard(false);
  }, [deck, role, isPro, onCardNodeUpdate, seedEvidenceKeys, userId, t]);

  // Commits a single mentor-authored note move once its drop-in animation
  // lands — creates a brand-new post-it (unlike commitMagicMove, which
  // relocates an existing card node). Declared here (after onNodeUpdate)
  // to avoid the TDZ error commitMagicMove would hit if it referenced
  // onNodeUpdate at its own, earlier declaration site.
  const commitMagicNote = useCallback((move) => {
    const noteId = `mentor-note-${move.phase}-${Date.now()}`;
    const newNote = {
      id: noteId,
      type: "postItNode",
      position: move.toFlow,
      data: {
        label: move.text,
        group: "note",
        id: noteId,
        onNodeUpdate,
        onNodeDelete,
        w: 200,
        h: 140,
        bg: "#F3EEFF",
        tag: move.phase,
        tagColor: PHASE_COLOR[move.phase] || "#9aa3c0",
      },
    };
    setNodes((nds) => [...nds, newNote]);
  }, [setNodes, onNodeUpdate, onNodeDelete]);

  // Mentor surfaced one or more key evidences: append to evidenceChips state
  // instead of creating canvas nodes. Task 2's ZoneLayer will render these.
  const handleEvidence = useCallback((evidences) => {
    const additions = {}; // phase -> new texts to append, built up first
    evidences.forEach((ev) => {
      const text = (ev.text || "").trim();
      if (!text) return;
      const phase = ZONE_BASE_POSITIONS[ev.phase] ? ev.phase : "integrazione_implementazione";
      const key = `${phase}::${text.toLowerCase()}`;
      if (evidenceKeysRef.current.has(key)) return;
      evidenceKeysRef.current.add(key);
      (additions[phase] || (additions[phase] = [])).push(text);
    });
    if (Object.keys(additions).length === 0) return;
    setEvidenceChips((prev) => {
      const next = { ...prev };
      Object.entries(additions).forEach(([phase, texts]) => {
        next[phase] = [...(next[phase] || []), ...texts];
      });
      return next;
    });
  }, []);

  const onLoad = useCallback((rfInstance) => {
    // console.log("React Flow Instance Loaded");
    setReactFlowInstance(rfInstance);
  }, []);

  const updateNodeText = (nodeId, newText) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, label: newText } };
        }
        return node;
      })
    );
  };

  // memo(({ id, data, isConnectable, selected, onNodeUpdate })
  const addResizableTextBox = () => {
    const newNode = {
      id: `text-box-${Date.now()}`,
      type: "resizableText",
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: {
        label: t('canvas.resizableText.editDefault'),
        group: "note",
        onNodeUpdate: onNodeUpdate,
      },
    };

    // Directly set the new node
    setNodes((nds) => [...nds, newNode]);
  };

  //---------backflip all cards
  const flipAllCards = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => {
        // Used cards stay front-face — only the still-in-pile cards flip.
        if (node.type === "customCardNode" && !node.data.isUsed) {
          return {
            ...node,
            data: {
              ...node.data,
              isFlipped: !node.data.isFlipped,  // Toggle the isFlipped property
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);

//---------backflip all cards

//------CREATE DECK
const createDeck = useCallback(() => {
  // Reset all cards to unused
  const allCards = nodes
    .filter(node => node.type === 'customCardNode')
    .map(node => ({
      id: node.id,
      label: node.data.label,
      group: node.data.group,
      image: node.data.image,
      descrizioneBreve: node.data.descrizioneBreve,
      freePreview: node.data.freePreview
    }));

  setUsedCards([]);
  setUnusedCards(allCards);
  setDeckRevealed(false);

  setNodes(nds =>
    nds.map((node, index) => {
      if (node.type === 'customCardNode') {
        return {
          ...node,
          position: pilePosition(index),
          // Back to the pile: face-down and flippable again.
          data: { ...node.data, isFlipped: true, isUsed: false },
        };
      }
      return node;
    })
  );
}, [nodes, setNodes]);
//------CREATE DECK

const createDecks = () => {
  const deckPositions = {
    A: { x: 50, y: 50 },
    B: { x: 300, y: 50 },
    C: { x: 550, y: 50 },
    D: { x: 800, y: 50 },
    E: { x: 1050, y: 50 },
  };

  const shiftAmount = 1; // The amount of shift for each card in the deck

  // Create a mapping to track the number of cards processed per group
  const groupCardCounts = { A: 0, B: 0, C: 0, D: 0, E: 0 };

  setNodes((nds) =>
    nds.map((node) => {
      const group = node.data.group;

      if (deckPositions[group]) {
        const currentShift = groupCardCounts[group] * shiftAmount;

        const updatedNode = {
          ...node,
          position: {
            x: deckPositions[group].x + currentShift,
            y: deckPositions[group].y + currentShift,
          },
          style: {
            ...node.style,
            zIndex: groupCardCounts[group], // Ensure correct stacking order
          },
        };

        groupCardCounts[group] += 1; // Increment the count for the current group

        return updatedNode;
      }

      return node;
    })
  );
};


  // Shuffle the deck and update the state
  const handleShuffleDeck = () => {
    setNodes((nds) => shuffleArray(nds));
    resetNodePositions()
  };

  const reorderNodes = (nodes, cardNames) => {
    // Create a mapping from card label to the original order based on cardNames array
    const cardOrderMap = cardNames.reduce((acc, card, index) => {
      acc[card.label] = index;
      return acc;
    }, {});
  
    // Sort the nodes array based on the original order in cardOrderMap
    return nodes.slice().sort((a, b) => {
      return cardOrderMap[a.data.label] - cardOrderMap[b.data.label];
    });
  };

   // Function to reorder the deck
   const handleReorderDeck = () => {
    if (!deck) return;
    setNodes((nds) => reorderNodes(nds, deck.cards.map(c => ({ label: c.label }))));
    resetNodePositions()
  };
//------CREATE DECK


  //----------EXPORT TO PNG--------------

    const captureFullCanvas = useCallback(async () => {
      const { toPng } = await import('html-to-image');
      const flowElement = document.querySelector('.react-flow');
      if (!flowElement) {
        console.error('React Flow element not found');
        return;
      }

      // Hide the minimap and zoom controls during capture
      const minimapElement = flowElement.querySelector('.react-flow__minimap');
      const controlsElement = flowElement.querySelector('.react-flow__controls');

      if (minimapElement) {
        minimapElement.style.visibility = 'hidden';
      }

      if (controlsElement) {
        controlsElement.style.visibility = 'hidden';
      }

      const nodes = flowElement.querySelectorAll('.react-flow__node');
      if (nodes.length === 0) {
        console.error('No nodes found in the React Flow container');
        return;
      }

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      nodes.forEach(node => {
        const rect = node.getBoundingClientRect();
        console.log(`Node ID: ${node.id}, Rect:`, rect);
        minX = Math.min(minX, rect.left + window.scrollX);
        maxX = Math.max(maxX, rect.right + window.scrollX);
        minY = Math.min(minY, rect.top + window.scrollY);
        maxY = Math.max(maxY, rect.bottom + window.scrollY);
      });

      console.log(`Computed canvas bounds: minX=${minX}, minY=${minY}, maxX=${maxX}, maxY=${maxY}`);

      const padding = 50; // Add padding around the canvas
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      const width = maxX - minX;
      const height = maxY - minY;

      console.log(`Canvas dimensions with padding: width=${width}, height=${height}`);

      // Apply a transformation to the viewport to pan to the top-left corner
      const viewport = flowElement.querySelector('.react-flow__viewport');
      viewport.style.transform = `translate(${Math.abs(minX)}px, ${Math.abs(minY)}px) scale(1)`;

      setTimeout(() => {
        toPng(flowElement, {
          backgroundColor: '#ffffff',
          width: width,
          height: height,
          style: {
            width: `${width}px`,
            height: `${height}px`,
            transform: `translate(0px, 0px) scale(1)` // No additional transform during capture
          },
        }).then(dataUrl => {
          const link = document.createElement('a');
          link.download = 'full-canvas-capture.png';
          link.href = dataUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('Canvas captured and downloaded.');
    
          // Restore the minimap and controls visibility
          if (minimapElement) {
            minimapElement.style.visibility = 'visible';
          }
          if (controlsElement) {
            controlsElement.style.visibility = 'visible';
          }
    
          // Restore the original viewport transformation
          viewport.style.transform = `translate(${minX}px, ${minY}px) scale(1)`;
    
        }).catch(error => {
          console.error('Failed to capture the full canvas:', error);
    
          // Restore the minimap and controls visibility even if capture fails
          if (minimapElement) {
            minimapElement.style.visibility = 'visible';
          }
          if (controlsElement) {
            controlsElement.style.visibility = 'visible';
          }
        });
      }, 500); // Wait for transformations to stabilize
    
    }, [nodes]);
    
    
    
    
    
    
    
    
    
    
    
                
    //----------EXPORT TO PNG--------------
                

  // const print_nodes = () => {
  //   console.log(nodes);
  // };

  // Live, area-local height the card grid currently occupies — re-derived
  // from actual node positions every render (not a ratchet), so ZoneLayer's
  // Math.max-based brainstormHeight naturally grows when more rows are
  // needed and shrinks back down on its own once cards are gone and
  // nothing else (the tree, or the area's own floor) needs the space.
  const scenarioCardsGridHeight = useMemo(
    () => (sessionMode === 'scenari_futuri' ? computeScenarioCardsGridHeight(nodes) : 0),
    [nodes, sessionMode]
  );
  // Same idea, notes column (bottom-right of the T) — independent from the
  // cards column so a burst of one kind doesn't inflate the other's space.
  const scenarioNotesGridHeight = useMemo(
    () => (sessionMode === 'scenari_futuri' ? computeScenarioNotesGridHeight(nodes) : 0),
    [nodes, sessionMode]
  );

  return (
    <div ref={reactFlowWrapper} className="miro-root" style={{ width: "100%", height: "100%" }}>
        <div className="canvas-container miro">
          {/* ---- session title (top-left) ----
              The title IS the session objective — inferred by the mentor from
              the chat, adopted once, then editable here. Click to rename. */}
          <div className="miro-brand">
            {editingTitle ? (
              <input
                ref={titleInputRef}
                className="miro-brand__session-input"
                value={sessionName === 'default_name' ? '' : sessionName}
                placeholder={t('canvas.session.titlePlaceholder')}
                onChange={(e) => { setSessionName(e.target.value); setTitleEdited(true); }}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingTitle(false); }}
              />
            ) : (
              <button
                type="button"
                className="miro-brand__session"
                title={sessionBrief?.obiettivo || sessionName || t('canvas.session.renameFallback')}
                onClick={() => setEditingTitle(true)}
              >
                {sessionName && sessionName !== 'default_name' ? sessionName : t('canvas.session.untitled')}
              </button>
            )}
          </div>

          {/* ---- search + group filters — now a sidebar popover, not a
              permanent top-center bar (see cardsPanelAnchor). The mode
              select that used to live here moved to the bottom bar. ---- */}
          <Popover
            open={!!cardsPanelAnchor} anchorEl={cardsPanelAnchor} onClose={() => setCardsPanelAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          >
            <div className="cards-panel">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="miro-search__icon" />
              <input
                type="text"
                className="miro-search__input"
                placeholder={t('canvas.cardsPanel.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              <span className="miro-divider" />
              <div className="group-chips">
                {(deck?.groups || []).map((g) => (
                  <ColorIcon key={g.id} color={g.color} label={g.id}
                    isActive={activeGroups.includes(g.id)} onClick={toggleGroup} disabled={!deckRevealed} />
                ))}
                <ColorIcon color="#FFD166" label="note" icon={faNoteSticky} text={t('canvas.groupChip.notes')} isActive={activeGroups.includes("note")} onClick={toggleGroup} />
              </div>
              <span className="miro-divider" />
              <button
                type="button"
                className="miro-search__eye"
                title={activeGroups.length === allGroupLabels.length ? t('canvas.cardsPanel.hideAll') : t('canvas.cardsPanel.showAll')}
                aria-label={activeGroups.length === allGroupLabels.length ? t('canvas.cardsPanel.hideAll') : t('canvas.cardsPanel.showAll')}
                onClick={toggleAllGroups}
                disabled={!deckRevealed}
              >
                <FontAwesomeIcon icon={activeGroups.length === allGroupLabels.length ? faEye : faEyeSlash} />
              </button>
            </div>
          </Popover>

          {/* ---- tools dock (left) ----
              Was two toolbars (this one + a top-right "actions" bar). The
              top-right bar only ever held the Mentor toggle plus 6
              infrequent file/session operations — merged here as a single
              column: Mentor stays the one accent-colored primary action,
              the file operations collapse into one "File" menu button
              instead of 6 permanently-visible icons. */}
          <div className="miro-dock">
            <button
              ref={mentorBtnRef}
              className={`mbtn mbtn--accent${isChatbotVisible ? " is-on" : ""}`}
              title={t('canvas.dock.mentor')}
              aria-label={t('canvas.dock.mentor')}
              onClick={toggleChatbot}
            >
              <Sparkle size={16} />
            </button>
            <button className="mbtn mbtn--has-menu" title={t('canvas.dock.searchFilter')} aria-label={t('canvas.dock.searchFilter')} onClick={(e) => setCardsPanelAnchor(e.currentTarget)}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
            <button className="mbtn mbtn--has-menu" title={t('canvas.dock.fileMenu')} aria-label={t('canvas.dock.fileMenu')} onClick={(e) => setFileMenuAnchor(e.currentTarget)}>
              <FontAwesomeIcon icon={faFolderOpen} />
            </button>

            <span className="miro-dock__sep" />

            <button className="mbtn mbtn--has-menu" title={t('canvas.dock.addSticky')} aria-label={t('canvas.dock.addSticky')} onClick={(e) => setStickyAnchor(e.currentTarget)}>
              <FontAwesomeIcon icon={faNoteSticky} />
            </button>
            <button className="mbtn" title={t('canvas.dock.addTextBox')} aria-label={t('canvas.dock.addTextBox')} onClick={addResizableTextBox}>
              <FontAwesomeIcon icon={faFont} />
            </button>

            <span className="miro-dock__sep" />

            <button className="mbtn" title={t('canvas.dock.collectDeck')} aria-label={t('canvas.dock.collectDeck')} onClick={createDeck}>
              <FontAwesomeIcon icon={faClone} />
            </button>
            <button className="mbtn" title={t('canvas.dock.decksByGroup')} aria-label={t('canvas.dock.decksByGroup')} onClick={createDecks}>
              <FontAwesomeIcon icon={faObjectGroup} />
            </button>
            <button className="mbtn" title={t('canvas.dock.shuffle')} aria-label={t('canvas.dock.shuffle')} onClick={handleShuffleDeck}>
              <FontAwesomeIcon icon={faShuffle} />
            </button>
            <button className="mbtn" title={t('canvas.dock.reorder')} aria-label={t('canvas.dock.reorder')} onClick={handleReorderDeck}>
              <FontAwesomeIcon icon={faRefresh} />
            </button>
            <button className="mbtn" title={t('canvas.dock.flipAll')} aria-label={t('canvas.dock.flipAll')} onClick={flipAllCards}>
              <FontAwesomeIcon icon={faRightLeft} />
            </button>

            <span className="miro-dock__sep" />

            <button
              className="mbtn mbtn--text"
              title={t('canvas.dock.linkType', { label: currentEdgeType.label })}
              aria-label={t('canvas.dock.linkType', { label: currentEdgeType.label })}
              onClick={toggleEdgeType}
            >
              <FontAwesomeIcon icon={faLink} />
            </button>
            <button className="mbtn" title={t('canvas.dock.lineStyle')} aria-label={t('canvas.dock.lineStyle')} onClick={toggleEdgeStyle}>
              {styleIcons[currentEdgeStyle.style]}
            </button>
            <button className="mbtn mbtn--text" title={t('canvas.dock.applyToEdge')} aria-label={t('canvas.dock.applyToEdge')} onClick={updateSelectedEdgeStyle}>
              ↳
            </button>
          </div>

          {saveFeedback && <div className="miro-toast">{saveFeedback}</div>}

          <div className="canvas">
            <ZoneLayer
              evidenceChips={evidenceChips}
              sessionMode={sessionMode}
              scenarioSplits={scenarioSplits}
              scenarioBranches={scenarioBranches}
              onAddScenarioSplit={addScenarioSplit}
              onAcceptScenarioSplit={acceptScenarioSplit}
              onDismissScenarioSplit={dismissScenarioSplit}
              onRenameScenarioBranch={renameScenarioBranch}
              onDeleteScenarioBranch={deleteScenarioBranch}
              matrixAxisLabels={matrixAxisLabels}
              onAxisLabelChange={(axis, text) => setMatrixAxisLabels((prev) => ({ ...prev, [axis]: text }))}
              onUpdateBranchMatrixPos={updateBranchMatrixPos}
              onUpdateBranchStyle={updateBranchStyle}
              onExportArea={exportAreaPng}
              cardsGridHeight={scenarioCardsGridHeight}
              notesGridHeight={scenarioNotesGridHeight}
              onScenarioTreeSizeChange={setScenarioTreeSize}
            />
            {isChatbotVisible && (
              isPro ? (
                <Chatbot
                  onShuffleDeck={handleShuffleDeck}
                  usedCards={usedCards}
                  unusedCards={unusedCards}
                  sessionId={sessionId}
                  onCardAction={handleCardAction}
                  onEvidence={handleEvidence}
                  onSplitAction={handleSplitAction}
                  onRequestSaveSession={() => saveSessionFile(false)}
                  scenarioSplits={scenarioSplits}
                  scenarioBranches={scenarioBranches}
                  onGenerateBrief={openConceptBrief}
                  initialBrief={sessionBrief}
                  onBrief={(b) => {
                    // Fold labeled+positioned scenario branches into the
                    // brief's direzione text, client-side only (scenari_futuri
                    // mode only — see describeScenarioBranches above).
                    const withScenarios = (b && sessionMode === 'scenari_futuri')
                      ? (() => {
                          // Strip any previously-appended fold first (see
                          // SCENARIO_BRANCHES_SENTINEL) — b.direzione is what
                          // comes back from the mentor, which likely already
                          // echoes the prior fold verbatim, so replacing
                          // rather than appending keeps direzione from
                          // growing by one copy of the block every turn.
                          const rawDirezione = b.direzione || '';
                          const sentinelIdx = rawDirezione.indexOf(SCENARIO_BRANCHES_SENTINEL);
                          const baseDirezione = sentinelIdx === -1 ? rawDirezione : rawDirezione.slice(0, sentinelIdx).replace(/\s+$/, '');
                          const fold = describeScenarioBranches();
                          const direzione = fold
                            ? (baseDirezione ? `${baseDirezione}\n${SCENARIO_BRANCHES_SENTINEL}\n${fold}` : `${SCENARIO_BRANCHES_SENTINEL}\n${fold}`)
                            : baseDirezione;
                          return { ...b, direzione };
                        })()
                      : b;
                    setSessionBrief(withScenarios);
                    // Adopt the mentor-inferred objective as the session title
                    // once (first non-empty value), then leave it stable — the
                    // 'default_name' guard blocks later overwrites, and a manual
                    // rename (titleEdited) locks it for good.
                    if (b && b.obiettivo && !titleEdited && sessionName === 'default_name') {
                      setSessionName(b.obiettivo);
                    }
                  }}
                  initialPhase={sessionPhase}
                  onPhase={(p) => setSessionPhase(p)}
                  initialMode={sessionMode}
                  onMode={(m) => setSessionMode(m)}
                  initialModeAskAttempts={sessionModeAskAttempts}
                  onModeAskAttempts={(n) => setSessionModeAskAttempts(n)}
                  initialPendingCheckpoint={pendingCheckpoint}
                  onPendingCheckpoint={(v) => setPendingCheckpoint(v)}
                  initialTeacherStage={sessionTeacherStage}
                  onTeacherStage={(v) => setSessionTeacherStage(v)}
                  initialMessages={sessionMessages}
                  onMessagesChange={setSessionMessages}
                />
              ) : (
                <MentorUpsell onClose={toggleChatbot} onUpgrade={() => navigate('/pricing')} />
              )
            )}
            <TemplatePicker open={templateOpen} onClose={() => setTemplateOpen(false)} onPick={loadTemplate} />
            <ConceptBrief
              open={briefOpen}
              onClose={() => setBriefOpen(false)}
              brief={briefData}
              onRegenerateSummary={regenerateSessionSummary}
              onTitleChange={(title) => {
                setSessionBrief((prev) => ({ ...(prev || {}), obiettivo: title }));
                setBriefData((prev) => (prev ? { ...prev, title } : prev));
              }}
            />
            <CardDetailPopup card={cardDetail} onClose={() => setCardDetail(null)} />
            <CanvasCoachmarks open={coachOpen} onClose={() => setCoachOpen(false)} onPickTemplate={() => { setCoachOpen(false); setTemplateOpen(true); }} />

            <Popover
              open={!!stickyAnchor} anchorEl={stickyAnchor} onClose={() => setStickyAnchor(null)}
              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            >
              <Box sx={{ p: 1.5, width: 196 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>{t('canvas.stickyPopover.shape')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  {Object.entries(STICKY_SHAPES).map(([key, s]) => (
                    <Box key={key} onClick={() => setStickyShape(key)} title={t(`canvas.stickyShapes.${key}`)}
                      sx={{
                        width: key === 'rect' ? 36 : 26, height: 26, bgcolor: '#e9ecf1', cursor: 'pointer',
                        borderRadius: key === 'circle' ? '50%' : '6px',
                        outline: stickyShape === key ? '2px solid #1FB6E8' : '2px solid transparent', outlineOffset: '2px',
                      }} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>{t('canvas.stickyPopover.color')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {withTints(NOTE_PALETTE).map((row) => (
                    <Box key={row[0]} sx={{ display: 'flex', gap: 0.75 }}>
                      {row.map((c) => (
                        <Box key={c} onClick={() => addStickyNote(stickyShape, c)} title={t('canvas.stickyPopover.addNote')}
                          sx={{
                            width: 22, height: 22, bgcolor: c, cursor: 'pointer',
                            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                            borderRadius: stickyShape === 'circle' ? '50%' : '6px',
                            transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.12)' },
                          }} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Popover>

            <Popover
              open={!!fileMenuAnchor} anchorEl={fileMenuAnchor} onClose={() => setFileMenuAnchor(null)}
              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            >
              <Box sx={{ py: 0.75, width: 210 }}>
                {[
                  { icon: faRefresh, label: t('canvas.fileMenu.new'), onClick: () => setPendingNewDashboard(true) },
                  { icon: faFolderOpen, label: t('canvas.fileMenu.openFromFile'), onClick: openSessionFile },
                  { icon: faFileArrowUp, label: t('canvas.fileMenu.saveToFile'), onClick: () => saveSessionFile(false) },
                  { icon: faSave, label: t('canvas.fileMenu.saveAsNew'), onClick: () => saveSessionFile(true) },
                  { icon: faImage, label: t('canvas.fileMenu.exportPng'), onClick: captureFullCanvas },
                  { icon: faFileExport, label: t('canvas.fileMenu.exportBrief'), onClick: openConceptBrief },
                ].map((item) => (
                  <Box
                    key={item.label}
                    onClick={() => { item.onClick(); setFileMenuAnchor(null); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1,
                      fontSize: '0.82rem', color: 'text.primary', cursor: 'pointer',
                      '&:hover': { bgcolor: '#f4f6fc' },
                    }}
                  >
                    <FontAwesomeIcon icon={item.icon} style={{ width: 14 }} />
                    {item.label}
                  </Box>
                ))}
              </Box>
            </Popover>

            {/* The objective is inferred from the chat (no manual field), so
                the onboarding nudge just points at the mentor to get talking.
                Positioned off the mentor toggle's measured rect. */}
            {!deckRevealed && !onboardDismissed && mentorBtnRect && (
              <Callout
                className="callout--objective"
                style={{ position: 'fixed', top: mentorBtnRect.top, left: mentorBtnRect.left }}
                text={t('canvas.onboarding.openMentorHint')}
                onDismiss={() => setOnboardDismissed(true)}
                onClick={() => { if (!isChatbotVisible) toggleChatbot(); }}
              />
            )}

            {/* scenari_futuri cinematic beat: the camera has already zoomed
                to the fork/cards/notes the mentor just added (see
                startScenarioBeat) — this names what happened and offers an
                immediate way back, on top of the hold-timer that fires the
                same return on its own. Fixed/top-center, non-invasive: it
                never blocks the canvas or requires a response. */}
            {scenarioBeat && (
              <>
                <Callout
                  className="callout--scenario-beat"
                  text={scenarioBeat.text}
                />
                <button
                  type="button"
                  className="scenario-beat-return"
                  onClick={endScenarioBeat}
                  title={t('canvas.scenarioBeat.returnTitle')}
                  aria-label={t('canvas.scenarioBeat.returnTitle')}
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} />
                </button>
              </>
            )}

            <ConfirmDialog
              open={!!pendingDraft}
              title={t('canvas.confirm.draft.title')}
              message={pendingDraft ? t('canvas.confirm.draft.message', { date: new Date(pendingDraft.savedAt).toLocaleString(dateLocale) }) : ''}
              confirmLabel={t('canvas.confirm.draft.confirm')}
              cancelLabel={t('canvas.confirm.draft.cancel')}
              onConfirm={() => resolveDraftPrompt(true)}
              onCancel={() => resolveDraftPrompt(false)}
            />

            <ConfirmDialog
              open={!!pendingTemplate}
              title={t('canvas.confirm.template.title')}
              message={t('canvas.confirm.template.message')}
              confirmLabel={t('canvas.confirm.template.confirm')}
              cancelLabel={t('canvas.confirm.template.cancel')}
              onConfirm={() => { applyTemplate(pendingTemplate); setPendingTemplate(null); }}
              onCancel={() => setPendingTemplate(null)}
            />

            <ConfirmDialog
              open={pendingNewDashboard}
              title={t('canvas.confirm.newDashboard.title')}
              message={t('canvas.confirm.newDashboard.message')}
              confirmLabel={t('canvas.confirm.newDashboard.confirm')}
              cancelLabel={t('canvas.confirm.newDashboard.cancel')}
              onConfirm={startNewDashboard}
              onCancel={() => setPendingNewDashboard(false)}
            />

            <ConfirmDialog
              open={!!pendingModeSwitch}
              title={t('canvas.confirm.modeSwitch.title')}
              message={t('canvas.confirm.modeSwitch.message')}
              confirmLabel={t('canvas.confirm.modeSwitch.confirm')}
              cancelLabel={t('canvas.confirm.modeSwitch.cancel')}
              onConfirm={() => applyModeSwitch(pendingModeSwitch)}
              onCancel={() => setPendingModeSwitch(null)}
            />

            <Snackbar open={nudgeOpen} autoHideDuration={9000} onClose={() => setNudgeOpen(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <Alert severity="info" onClose={() => setNudgeOpen(false)}
                action={<MuiButton color="inherit" size="small" onClick={() => { if (!isChatbotVisible) toggleChatbot(); setNudgeOpen(false); }}>{t('canvas.nudge.openMentor')}</MuiButton>}>
                {t('canvas.nudge.message')}
              </Alert>
            </Snackbar>
            <ReactFlow
              onLoad={onLoad}
              className="miro-flow"
              nodes={filteredNodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onEdgeClick={onEdgeClick}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              onNodeDragStop={onNodeDragStop}
              nodesDraggable={true}
              minZoom={0.15}
            >
              <Background variant="dots" gap={26} size={1.6} color="#E3D4C4" />
            </ReactFlow>
            <MagicCursor
              moves={magicMoves}
              onCommit={(move) => (move.kind === "note" ? commitMagicNote(move) : commitMagicMove(move))}
              onComplete={() => setMagicMoves(null)}
            />
            {/* ---- single bottom bar: dashboard-area jump + zoom (was
                react-flow's own <Controls> block) + brainstorming mode,
                merged into one small pill instead of two competing bars.
                See AreaJumpButton.js — it owns the AREAS/jump logic, this
                just wires in the zoom/mode bits InfiniteCanvas owns. ---- */}
            <AreaJumpButton
              sessionMode={sessionMode}
              zoom={zoom}
              onZoomOut={() => reactFlowInstance.zoomOut()}
              onZoomIn={() => reactFlowInstance.zoomIn()}
              onFitView={() => reactFlowInstance.fitView({ padding: 0.15, duration: 500 })}
              onModeChange={(e) => requestModeSwitch(e.target.value)}
              modeOptions={Object.entries(getModeMeta(t)).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            />
            <button
              type="button"
              className="export-png-btn"
              title={t('canvas.exportBrainstorm.title')}
              onClick={exportBrainstormPng}
              style={{
                position: 'fixed', bottom: 24, right: 240, zIndex: 900,
                width: 40, height: 40, borderRadius: '50%', border: '1px solid #EFE7DA',
                background: 'rgba(251, 248, 244, 0.97)', cursor: 'pointer', fontSize: 16,
                boxShadow: '0 6px 16px rgba(40,50,90,0.14)',
              }}
            >
              ⬇
            </button>
            <DeckPileButton
              visible={!deckRevealed}
              onReveal={revealDeck}
              showHint={!deckRevealed && !!(sessionBrief && sessionBrief.obiettivo) && !onboardDismissed}
            />
            {isPro && (
              <BrainstormWidget brainstorm={brainstorm} setBrainstorm={setBrainstorm} cards={usedCards} />
            )}
            {sessionMode === 'mappatura' && (
              <EvidenceTray evidenceChips={evidenceChips} />
            )}
          </div>
        </div>
    </div>
  );
};

export default InfiniteCanvas;
